import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import {
	MdArrowBackIosNew,
	MdCalendarToday,
	MdChevronLeft,
	MdChevronRight,
	MdDeleteOutline,
	MdKeyboardArrowDown,
	MdKeyboardArrowUp,
	MdClose,
	MdOutlineEdit,
	MdOutlineWarningAmber,
	MdSettings,
	MdVisibility,
} from 'react-icons/md';
import { IoIosSearch } from 'react-icons/io';
import { Link } from 'react-router-dom';
import NavBar from '../NavBar';
import {
	requestAdicionarVacanciaAmbienteMultiprofissional,
	requestFiltrosBuscaEscalaMultiprofissional,
	requestAplicarAcoesAmbienteMultiprofissional,
	requestAbrirAmbienteEscalaMultiprofissional,
	requestDetalharVinculoMultiprofissional,
	requestContextosOrigemMultiprofissional,
	requestDetalharLinhaMultiprofissional,
	requestBuscarProfissionalEscalaMultiprofissional,
	requestCatalogosEscalaSelectsMultiprofissional,
	requestOpcoesAmbientePreenchimentoMultiprofissional,
	requestPreverAcoesAmbienteMultiprofissional,
	requestAtualizarVinculoProfissionalMultiprofissional,
	requestSalvarProfissionalAmbienteMultiprofissional,
	requestVinculosEscalaMultiprofissional,
} from '../../services/escalaMultiprofissionalRequests';
import {
	canManageEscalaMultiprofissional,
	isDeveloperUser,
} from '../../utils/escalaFeatures';
import SearchableSetorSelect from '../Escalas Utils/SearchableSetorSelect';
import NovoProfissionalMultiprofissionalModal, {
	HourRangeControl,
} from './NovoProfissionalMultiprofissionalModal';
import { sortSetoresPorAndar } from '../../utils/escalaSetorOrdering';

const TABLE_BODY_HEIGHT = 510;
const TABLE_ROW_HEIGHT = 42.5;
const MINIMUM_CALENDAR_ROWS = Math.ceil(TABLE_BODY_HEIGHT / TABLE_ROW_HEIGHT);
const EDIT_PANEL_WIDTH = 170;
const ALL_SECTORS_VALUE = '__todos__';
const ALL_SECTORS_OPTION = { value: ALL_SECTORS_VALUE, label: 'Todos' };
const isAllSectorsValue = (value = '') => String(value) === ALL_SECTORS_VALUE;

const FALLBACK_LEGEND = [
	{ color: 'bg-[#4D6C92]', label: 'Dia atribuído' },
	{ color: 'bg-white border border-[#CBD5E1]', label: 'Não atribuído' },
	{ color: 'bg-[#BD504E]', label: 'Vacância' },
	{ color: 'bg-[#6ACEF8]', label: 'Férias' },
	{ color: 'bg-[#FB965C]', label: 'Atestado' },
	{ color: 'bg-[#7DBA7D]', label: 'Folga' },
	{ color: 'bg-[#A98BDD]', label: 'Licença' },
	{ color: 'bg-[#98736A]', label: 'Troca' },
	{ color: 'bg-[#888888]', label: 'Falta' },
];

const FALLBACK_LEGEND_TEXT = [
	{ abbreviation: 'D', label: 'Diurno' },
	{ abbreviation: 'N', label: 'Noturno' },
	{ abbreviation: '24h', label: '24 Horas' },
];

const getDefaultMiniTabs = () => ({
	ferias: false,
	atestado: false,
	folga: false,
	licenca: false,
	troca: false,
	falta: false,
});

const waitForMinimumLoadingTime = (startedAt, minimumMilliseconds) => {
	const remaining = Math.max(
		0,
		minimumMilliseconds - (Date.now() - startedAt),
	);
	return remaining
		? new Promise((resolve) => setTimeout(resolve, remaining))
		: Promise.resolve();
};

const getErrorMessage = (result, fallback) => {
	if (!result?.error) return '';
	const issues = (result.error.issues || [])
		.map((item) => item?.mensagem || item?.message || '')
		.filter(Boolean)
		.join(' ');
	return issues || result.error.message || fallback;
};

const getText = (...values) => {
	for (const value of values) {
		if (value == null) continue;
		if (typeof value === 'object') {
			const nested = getText(
				value.nome,
				value.nome_exibicao,
				value.label,
				value.descricao,
			);
			if (nested) return nested;
			continue;
		}
		const text = String(value).trim();
		if (text) return text;
	}
	return '--';
};

const formatUnitLabel = (unit = {}) => {
	const label = getText(
		unit.nome,
		unit.nome_fantasia,
		unit.unidade,
		unit.label,
		unit.cnes,
	);
	return normalizeSearch(label) === 'cer jacarepagua'
		? 'Hospital Cardoso Fontes'
		: label;
};

const normalizeUnit = (item = {}) => ({
	value: String(
		item.cnes ||
			item.cnes_estabelecimento ||
			item.id ||
			item.codigo ||
			item.value ||
			'',
	),
	label: formatUnitLabel(item),
});

const normalizeUnits = (payload) => {
	const list = Array.isArray(payload)
		? payload
		: Array.isArray(payload?.results)
			? payload.results
			: Array.isArray(payload?.estabelecimentos)
				? payload.estabelecimentos
				: Array.isArray(payload?.unidades)
					? payload.unidades
			: [];
	const byCnes = new Map();
	list.forEach((item) => {
		const unit = normalizeUnit(item);
		if (unit.value && unit.label && !byCnes.has(unit.value)) {
			byCnes.set(unit.value, unit);
		}
	});
	return Array.from(byCnes.values());
};

const normalizeOption = (item = {}) => ({
	value: String(item.id || item.codigo || item.value || ''),
	label: getText(
		item.nome_exibicao,
		item.nome,
		item.label,
		item.descricao,
		item.identificador,
		item.id,
	),
	identificador: String(item.identificador || item.codigo || ''),
});

// O contrato operacional de `catalogos-escala-selects` coloca os setores em
// `estrutura.setores`; `nome_exibicao` é o texto da opção e `id` é o valor
// que precisa seguir para os endpoints da escala.
const getCatalogStructureSectors = (catalogs = {}) =>
	Array.isArray(catalogs?.estrutura?.setores)
		? catalogs.estrutura.setores
		: Array.isArray(catalogs?.setores)
			? catalogs.setores
			: [
					...(Array.isArray(catalogs?.tipos_secao) ? catalogs.tipos_secao : []),
					...(Array.isArray(catalogs?.servicos) ? catalogs.servicos : []),
				].flatMap((group) =>
					Array.isArray(group?.setores) ? group.setores : [],
				);

const normalizeCatalogSectorOption = (setor = {}) => {
	const nomeExibicao = getText(
		setor?.nome_exibicao,
		setor?.nome,
		setor?.identificador,
		setor?.label,
		setor?.id,
	);
	return {
		value: String(setor?.id ?? setor?.value ?? setor?.codigo ?? ''),
		label: nomeExibicao,
		nome_exibicao: nomeExibicao,
		identificador: String(setor?.identificador || setor?.codigo || ''),
	};
};

const normalizeSearch = (value = '') =>
	String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

const WEEK_DAYS = [
	{ value: 1, label: 'Seg' },
	{ value: 2, label: 'Ter' },
	{ value: 3, label: 'Qua' },
	{ value: 4, label: 'Qui' },
	{ value: 5, label: 'Sex' },
	{ value: 6, label: 'Sáb' },
	{ value: 7, label: 'Dom' },
];

const JORNADA_WEEK_DAYS = [
	{ value: 1, label: 'Segunda' },
	{ value: 2, label: 'Terça' },
	{ value: 3, label: 'Quarta' },
	{ value: 4, label: 'Quinta' },
	{ value: 5, label: 'Sexta' },
	{ value: 6, label: 'Sábado' },
	{ value: 7, label: 'Domingo' },
];

const RECURRENCE_OPTIONS = [
	{
		value: 'SEMANAL_12H',
		label: '12H (segunda à sexta)',
		tipo_recorrencia: 'SEMANAL',
		preset_ciclo: 'SEMANAL',
	},
	{
		value: 'SEMANAL',
		label: 'Semanal',
		tipo_recorrencia: 'SEMANAL',
		preset_ciclo: 'SEMANAL',
	},
	{
		value: 'CICLO_12X36',
		label: 'Ciclo-12x36',
		tipo_recorrencia: 'CICLO_CONTINUO',
		preset_ciclo: '12x36',
	},
	{
		value: 'CICLO_12X60',
		label: 'Ciclo-12x60',
		tipo_recorrencia: 'CICLO_CONTINUO',
		preset_ciclo: '12x60',
	},
	{
		value: 'CICLO_24X72',
		label: 'Ciclo-24x72',
		tipo_recorrencia: 'CICLO_CONTINUO',
		preset_ciclo: '24x72',
	},
	{
		value: 'CICLO_24X120',
		label: 'Ciclo-24x120',
		tipo_recorrencia: 'CICLO_CONTINUO',
		preset_ciclo: '24x120',
	},
	{
		value: 'ROTACAO_PERIODICA',
		label: 'Rotação periódica',
		tipo_recorrencia: 'ROTACAO_PERIODICA',
		preset_ciclo: 'ROTACAO_PERIODICA',
	},
	{
		value: 'DATAS_ESPECIFICAS',
		label: 'Datas específicas',
		tipo_recorrencia: 'DATAS_ESPECIFICAS',
		preset_ciclo: 'PERSONALIZADO',
	},
];

const emptyProfessional = () => ({
	cpf: '',
	nome: '',
	categoria: '',
	vinculoTipo: '',
	admissao: '',
	setor: '',
	cargo: '',
	numeroConselho: '',
	ufConselho: 'RJ',
	telefone: '',
	regime: 'ROTINA',
	recorrencia: 'SEMANAL',
	vigenciaInicio: '',
	horaInicio: '',
	horaFim: '',
	diasSemana: [],
	diasSemanaHorarios: {},
	datasEspecificas: [],
	semanasRotacao: [
		[1, 2, 3, 4, 5],
		[1, 2, 3, 4, 5],
	],
});

const emptyVacancy = () => ({
	categoria: '',
	vinculoTipo: '',
	setor: '',
	dataInicio: '',
	observacao: '',
	regime: 'ROTINA',
	recorrencia: 'SEMANAL',
	horaInicio: '07:00',
	horaFim: '19:00',
	diasSemana: [1, 2, 3, 4, 5],
});

const toIsoDate = (value = '') =>
	String(value || '').match(/^\d{4}-\d{2}-\d{2}/)?.[0] || '';

const getCurrentIsoDate = () => {
	const today = new Date();
	return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

const formatBrazilianDate = (value = '') => {
	const iso = toIsoDate(value);
	if (!iso) return '--/--/----';
	const [year, month, day] = iso.split('-');
	return `${day}/${month}/${year}`;
};

const getMonthReference = (value = new Date()) => {
	const date =
		value instanceof Date
			? value
			: new Date(`${toIsoDate(value) || value}T12:00:00`);
	const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
	return `${safeDate.getFullYear()}-${String(safeDate.getMonth() + 1).padStart(2, '0')}-01`;
};

const getMonthDays = (calendario = []) => {
	if (Array.isArray(calendario) && calendario.length) return calendario;
	const now = new Date();
	const total = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
	return Array.from({ length: total }, (_, index) => ({
		data: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(index + 1).padStart(2, '0')}`,
	}));
};

const getLineName = (linha = {}) =>
	getText(
		linha.nome_profissional,
		linha.profissional?.nome,
		linha.resumo?.titulo,
		linha.nome,
		'Profissional',
	);

const getLineProfessionalDetails = (linha = {}) => {
	const dados = linha?.dados || linha?.resumo?.dados || {};
	const vinculo =
		linha?.vinculo_profissional ||
		dados?.vinculo_profissional ||
		linha?.vinculo_estabelecimento ||
		{};
	const profissional =
		linha?.profissional ||
		vinculo?.profissional ||
		dados?.profissional ||
		{};
	const categoria =
		linha?.categoria || vinculo?.categoria || dados?.categoria || {};
	const tipoVinculo =
		vinculo?.vinculo_tipo ||
		vinculo?.tipo_vinculo ||
		linha?.vinculo_tipo ||
		dados?.vinculo_tipo ||
		{};
	return {
		cpf:
			profissional?.cpf || vinculo?.cpf || linha?.cpf || dados?.cpf || '',
		nome:
			profissional?.nome ||
			linha?.nome_profissional ||
			linha?.resumo?.titulo ||
			linha?.nome ||
			'',
		setor:
			linha?.setor?.id ||
			linha?.setor_id ||
			dados?.setor?.id ||
			dados?.setor_id ||
			'',
		categoria:
			categoria?.id ||
			linha?.categoria_id ||
			vinculo?.categoria_id ||
			dados?.categoria_id ||
			'',
		vinculoTipo:
			tipoVinculo?.id ||
			vinculo?.vinculo?.id ||
			vinculo?.vinculo_tipo_id ||
			vinculo?.tipo_vinculo_id ||
			linha?.vinculo_tipo_id ||
			dados?.vinculo_tipo_id ||
			'',
		admissao:
			vinculo?.data_admissao ||
			vinculo?.admissao ||
			linha?.data_admissao ||
			dados?.data_admissao ||
			'',
		cargo:
			vinculo?.cargo ||
			profissional?.cargo ||
			linha?.cargo ||
			dados?.cargo ||
			'',
		numeroConselho:
			profissional?.numero_registro ||
			profissional?.numero_conselho ||
			profissional?.conselho?.numero ||
			vinculo?.numero_registro ||
			linha?.numero_registro ||
			linha?.numero_conselho ||
			dados?.numero_registro ||
			dados?.numero_conselho ||
			'',
		ufConselho:
			profissional?.uf_conselho ||
			profissional?.uf ||
			vinculo?.uf_conselho ||
			linha?.uf_conselho ||
			linha?.uf ||
			dados?.uf_conselho ||
			'RJ',
		telefone:
			profissional?.telefone ||
			profissional?.contato?.telefone ||
			vinculo?.telefone ||
			linha?.telefone ||
			dados?.telefone ||
			'',
	};
};

const getLineVinculoId = (linha = {}) =>
	linha?.vinculo_profissional?.id ||
	linha?.vinculo_profissional_id ||
	linha?.profissional?.vinculo_id ||
	linha?.vinculo_id ||
	linha?.profissional_vinculo_id ||
	linha?.celulas?.flatMap((cell) => cell?.cards || cell?.itens || [])?.find(Boolean)
		?.vinculo_profissional_id ||
	linha?.celulas?.flatMap((cell) => cell?.cards || cell?.itens || [])?.find(Boolean)
		?.vinculo_id ||
	null;

const getLineProfessionalGroupKey = (line = {}) => {
	const professionalId =
		line?.profissional?.id ||
		line?.vinculo_profissional?.profissional_id ||
		line?.vinculo_profissional?.profissional?.id;
	if (professionalId) return `id-${professionalId}`;
	const cpf = getCpfDigits(getLineProfessionalDetails(line).cpf);
	if (cpf) return `cpf-${cpf}`;
	return `nome-${normalizeSearch(getLineName(line))}`;
};

const getSearchResultVinculoIds = (result = {}) =>
	[
		getLineVinculoId(result),
		...(Array.isArray(result?.vinculos) ? result.vinculos : []).map(
			getLineVinculoId,
		),
	]
		.filter(Boolean)
		.map(String);

const getContextLines = (result = {}) => {
	const contexts = Array.isArray(result?.contextos_operacionais)
		? result.contextos_operacionais
		: [result?.contextos_operacionais].filter(Boolean);
	return [
		...[result?.linha].filter(Boolean),
		...(Array.isArray(result?.linhas) ? result.linhas : []),
		...(Array.isArray(result?.ambiente?.linhas)
			? result.ambiente.linhas
			: []),
		...contexts.flatMap((context) => {
			if (Array.isArray(context?.linhas)) return context.linhas;
			return context?.linha ? [context.linha] : [];
		}),
	];
};

const getLineRegimeVinculoId = (linha = {}) =>
	linha?.regime_vinculo_id ||
	linha?.regime_escala?.regime_vinculo_id ||
	linha?.regimeEscala?.regime_vinculo_id ||
		null;

const consolidateProfessionalLines = (lines = []) => {
	const groups = new Map();
	lines.forEach((line, index) => {
		const vinculoId = getLineVinculoId(line);
		const professionalKey = getLineProfessionalGroupKey(line);
		const key = !isVacancyLine(line)
			? `${professionalKey}|vinculo-${vinculoId || 'indefinido'}`
			: `linha-${line?.linha_id || index}`;
		if (!groups.has(key)) {
			groups.set(key, {
				...line,
				setores_linhas: [line],
				celulas: Array.isArray(line?.celulas) ? [...line.celulas] : [],
			});
			return;
		}
		const current = groups.get(key);
		const cellsByDate = new Map(
			(current.celulas || []).map((cell) => [toIsoDate(cell?.data), cell]),
		);
		(line.celulas || []).forEach((cell) => {
			const date = toIsoDate(cell?.data);
			const previous = cellsByDate.get(date);
			if (!previous) {
				cellsByDate.set(date, cell);
				return;
			}
			const previousCards = Array.isArray(previous?.cards)
				? previous.cards
				: Array.isArray(previous?.itens) ? previous.itens : [];
			const nextCards = Array.isArray(cell?.cards)
				? cell.cards
				: Array.isArray(cell?.itens) ? cell.itens : [];
			const nextStatus = String(cell?.status || '').trim().toUpperCase();
			const nextCellIsAllocated =
				nextCards.some(hasRenderableCard) ||
				cell?.ocupado === true ||
				Boolean(nextStatus && !['NAO_ATRIBUIDO', 'SEM_ALOCACAO', 'SEM ALOCACAO'].includes(nextStatus));
			cellsByDate.set(date, {
				...previous,
				...(nextCellIsAllocated ? cell : {}),
				cards: [...previousCards, ...nextCards],
				ocupado: previous?.ocupado === true || cell?.ocupado === true,
			});
		});
		groups.set(key, {
			...current,
			setores_linhas: [...current.setores_linhas, line],
			celulas: Array.from(cellsByDate.values()),
		});
	});
	return Array.from(groups.values());
};

const isVacancyLine = (linha = {}) =>
	String(linha?.tipo_item || linha?.tipo || '').toUpperCase() === 'VACANCIA';

const isHistoricalReadOnlyLine = (linha = {}, monthReference = '') => {
	if (!linha || isVacancyLine(linha)) return false;
	const regime = linha.regime_escala || linha.regimeEscala || {};
	const endDate = toIsoDate(
		regime.vigencia_fim || regime.data_fim || linha.vigencia_fim || '',
	);
	const monthStart = toIsoDate(monthReference);
	const today = getCurrentIsoDate();
	if (!endDate || !monthStart || endDate > today) return false;
	const nextMonthDate = new Date(`${monthStart}T12:00:00`);
	if (Number.isNaN(nextMonthDate.getTime())) return false;
	nextMonthDate.setMonth(nextMonthDate.getMonth() + 1, 1);
	const nextMonth = `${nextMonthDate.getFullYear()}-${String(nextMonthDate.getMonth() + 1).padStart(2, '0')}-01`;
	const hasPastCard = (linha.celulas || []).some((cell) => {
		const date = toIsoDate(cell?.data);
		const status = String(cell?.status || '').trim().toUpperCase();
		const cards = Array.isArray(cell?.cards) ? cell.cards : [cell?.card].filter(Boolean);
		return Boolean(
			date && date <= endDate && date <= today &&
			(cards.some(hasRenderableCard) || cell?.sugerido === true || status === 'SUGERIDO'),
		);
	});
	return hasPastCard && endDate >= monthStart && endDate < nextMonth;
};

const hasRenderableCard = (card) =>
	Boolean(
		card &&
		typeof card === 'object' &&
		Object.values(card).some(
			(value) => value !== undefined && value !== null && value !== '',
		),
	);

const isAllocatedCell = ({ cell, card, status }) => {
	const normalizedStatus = String(status || '')
		.trim()
		.toUpperCase();
	// A resposta do ambiente traz `{}` em `cards` para células sem jornada.
	// Como objetos vazios são truthy em JavaScript, usar `Boolean(card)` fazia
	// a grade exibir "D" em todos os dias, mesmo com status NAO_ATRIBUIDO.
	if (
		['NAO_ATRIBUIDO', 'SEM_ALOCACAO', 'SEM ALOCACAO'].includes(
			normalizedStatus,
		)
	) {
		return false;
	}

	return (
		hasRenderableCard(card) ||
		cell?.ocupado === true ||
		Boolean(normalizedStatus)
	);
};

const getCardDisplayLabel = ({ card = {}, cell = {}, status = '' } = {}) => {
	const normalizedStatus = String(status || '')
		.trim()
		.toUpperCase();
	const explicitLabel = getText(
		card?.sigla,
		card?.abreviacao,
		card?.abbreviation,
		card?.codigo_exibicao,
		cell?.sigla,
		cell?.abreviacao,
	);
	if (explicitLabel !== '--') return explicitLabel;

	const regime = normalizeSearch(
		card?.regime || card?.regime_nome || cell?.regime || cell?.regime_nome,
	);
	const turno = normalizeSearch(
		card?.turno ||
			card?.turno_nome ||
			card?.tipo_turno ||
			cell?.turno ||
			cell?.turno_nome,
	);
	if (
		normalizedStatus.includes('24') ||
		regime.includes('24') ||
		turno.includes('24')
	)
		return '24h';
	if (
		normalizedStatus.includes('NOTUR') ||
		regime.includes('NOTUR') ||
		turno.includes('NOTUR')
	)
		return 'N';
	if (
		normalizedStatus.includes('DIURN') ||
		regime.includes('ROTINA') ||
		turno.includes('DIURN')
	)
		return 'D';
	if (normalizedStatus.includes('VAC')) return 'D';
	return 'D';
};

const createIdempotencyKey = (prefix = 'escala-multiprofissional') =>
	typeof window !== 'undefined' &&
	typeof window.crypto?.randomUUID === 'function'
		? `${prefix}-${window.crypto.randomUUID()}`
		: `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const getCellForDay = (linha = {}, isoDate) =>
	(linha.celulas || []).find((cell) => toIsoDate(cell?.data) === isoDate) ||
	null;

const formatPhone = (value = '') => {
	const digits = String(value).replace(/\D/g, '').slice(0, 11);
	if (digits.length <= 2) return digits ? `(${digits}` : '';
	if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
	if (digits.length <= 10)
		return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
	return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const getCpfDigits = (value = '') =>
	String(value).replace(/\D/g, '').slice(0, 11);

const formatCpf = (value = '') => {
	const digits = getCpfDigits(value);
	if (digits.length <= 3) return digits;
	if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
	if (digits.length <= 9)
		return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
	return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const normalizeRecurrenceOption = (item = {}) => {
	const preset = String(
		item.preset_ciclo || item.codigo || item.value || '',
	).trim();
	const type = String(item.tipo_recorrencia || '')
		.trim()
		.toUpperCase();
	if (!preset && !type) return null;
	const canonicalPreset = preset.toUpperCase().replace(/^CICLO_/, '');
	return {
		value:
			type === 'SEMANAL'
				? 'SEMANAL'
				: type === 'ROTACAO_PERIODICA'
					? 'ROTACAO_PERIODICA'
					: type === 'DATAS_ESPECIFICAS'
						? 'DATAS_ESPECIFICAS'
						: `CICLO_${canonicalPreset.replace(/[^A-Z0-9]/g, '_')}`,
		label: getText(
			item.nome_exibicao,
			item.nome,
			item.label,
			canonicalPreset === 'SEMANAL' ? 'Semanal' : `Ciclo-${preset}`,
		),
		tipo_recorrencia:
			type ||
			(canonicalPreset === 'SEMANAL' ? 'SEMANAL' : 'CICLO_CONTINUO'),
		preset_ciclo:
			preset ||
			(type === 'SEMANAL'
				? 'SEMANAL'
				: type === 'DATAS_ESPECIFICAS'
					? 'PERSONALIZADO'
					: type),
	};
};

const getRecurrenceOptionsFromPayload = (payload) => {
	const candidates =
		[
			payload?.modalidades,
			payload?.recorrencias,
			payload?.presets_ciclo,
			payload?.ciclos,
			payload?.opcoes?.recorrencias,
		].find(Array.isArray) || [];
	const mapped = candidates.map(normalizeRecurrenceOption).filter(Boolean);
	const byValue = new Map(
		[...RECURRENCE_OPTIONS, ...mapped].map((item) => [item.value, item]),
	);
	return Array.from(byValue.values());
};

function JornadaSetorForm({
	form,
	setorName,
	sectors,
	regimes,
	recurrenceOptions,
	isNewSector = false,
	onChange,
	onToggleDay,
	onChangeDayTime,
	onAddSpecificDate,
	onChangeSpecificDate,
	onRemoveSpecificDate,
	onCloseNewSector,
	onDeleteSector,
}) {
	const [collapsed, setCollapsed] = useState(false);
	if (!form) return null;
	const recurrenceType =
		recurrenceOptions.find((item) => item.value === form.recorrencia)
			?.tipo_recorrencia || 'SEMANAL';
	const addProfessionalRecurrenceValues = new Set([
		'SEMANAL_12H',
		'CICLO_12X60',
		'SEMANAL',
		'CICLO_24X120',
	]);
	const visibleRecurrenceOptions = recurrenceOptions.filter((item) =>
		addProfessionalRecurrenceValues.has(String(item.value)),
	);
	const isWeekly = recurrenceType === 'SEMANAL';
	const recurrenceLabel =
		recurrenceOptions.find((item) => item.value === form.recorrencia)
			?.label || 'PERSONALIZADO';
	return (
		<div
			className={
				isNewSector
					? 'overflow-visible rounded-md border border-dashed border-[#B8C7D9] bg-[#F8FAFC] p-3'
					: 'overflow-visible rounded-md bg-[#004A80] shadow-sm'
			}>
			{isNewSector ? (
				<div className='mb-2 flex items-center justify-between gap-3'>
					<span className='min-w-0 truncate text-[13px] font-semibold text-[#13335A]'>
						Adicionar em outro setor
					</span>
					<button
						type='button'
						onClick={onCloseNewSector}
						className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[22px] leading-none text-gray-400 transition-colors hover:text-[#BD504E]'
						aria-label='Fechar bloco de adicionar em outro setor'>
						&times;
					</button>
				</div>
			) : (
				<div className={`flex min-h-[38px] w-full items-center bg-[#004A80] text-white ${collapsed ? 'rounded-md' : 'rounded-t-md'}`}>
					<button type='button' onClick={() => setCollapsed((current) => !current)} className='flex min-h-[38px] min-w-0 flex-1 items-center justify-between px-3 py-2 text-left' aria-expanded={!collapsed}>
						<span className='min-w-0 truncate text-[13px] font-semibold tracking-wide text-white'>{setorName}</span>
						<span className='flex items-center gap-2 truncate text-[11px] font-medium text-white/80'>
							<span>{form.regime} • {formatBrazilianDate(form.vigenciaInicio)}</span>
							{collapsed ? <MdKeyboardArrowDown className='h-5 w-5 shrink-0 text-white' /> : <MdKeyboardArrowUp className='h-5 w-5 shrink-0 text-white' />}
						</span>
					</button>
					<button type='button' onClick={onDeleteSector} aria-label={`Excluir setor ${setorName}`} title='Excluir este setor da escala do profissional' className='mr-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/90 transition hover:bg-white/15 hover:text-white'>
						<MdClose className='h-4 w-4' />
					</button>
				</div>
			)}
			<div className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${collapsed && !isNewSector ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'}`} aria-hidden={collapsed && !isNewSector}>
				<div className='min-h-0 overflow-hidden'>
				<div
				className={
					isNewSector
						? ''
						: 'rounded-b-md border border-[#004A80] bg-white p-3'
				}>
				<div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4'>
					{isNewSector && (
						<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
							<span>Setor</span>
							<SearchableSetorSelect
								value={form.setor}
								options={sectors}
								onChange={(value) => onChange('setor', value)}
								placeholder='Selecione o setor'
								emptyMessage='Nenhum setor encontrado'
								menuPortal
								className='h-[32px] rounded-md border border-gray-200 bg-white px-2 text-[12px] font-normal text-[#4F5257] outline-none'
							/>
						</label>
					)}
					<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
						<span>Recorrência</span>
						<select
							value={form.recorrencia}
							onChange={(event) =>
								onChange('recorrencia', event.target.value)
							}
							className='h-[32px] rounded-md border border-gray-200 bg-white px-2 text-[12px] font-normal text-[#4F5257] outline-none'>
							{(visibleRecurrenceOptions.length ? visibleRecurrenceOptions : recurrenceOptions).map((item) => (
								<option
									key={item.value}
									value={item.value}>
									{item.label}
								</option>
							))}
						</select>
					</label>
					<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
						<span>Regime</span>
						<select
							value={form.regime}
							onChange={(event) =>
								onChange('regime', event.target.value)
							}
							className='h-[32px] rounded-md border border-gray-200 bg-white px-2 text-[12px] font-normal text-[#4F5257] outline-none'>
							{regimes.map((item) => (
								<option
									key={item.value}
									value={item.value}>
									{item.label}
								</option>
							))}
						</select>
					</label>
					{!isNewSector && (
						<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
							<span>Última atualização</span>
							<input
								type='date'
								value={form.vigenciaInicio || ''}
								readOnly
								className='h-[32px] w-full cursor-not-allowed rounded-md border border-gray-200 bg-[#F3F4F6] px-2 text-[12px] font-normal text-[#6B7280] outline-none'
							/>
						</label>
					)}
					<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
						<span>Data inicial</span>
						<input
							type='date'
							value={form.vigenciaInicio || ''}
							disabled
							className='h-[32px] w-full cursor-not-allowed rounded-md border border-gray-200 bg-[#F3F4F6] px-2 text-[12px] font-normal text-[#6B7280] outline-none'
						/>
					</label>
				</div>
				<div className='mt-3 grid grid-cols-1 gap-3 xl:grid-cols-[1fr_260px]'>
					<div className='rounded-md border border-[#B8C7D9] bg-white px-2 py-2 shadow-sm'>
						<p className='mb-2 text-[12px] font-medium text-[#13335A]'>
							{isWeekly
								? 'Dias recorrentes'
								: 'Escala do profissional'}
						</p>
						{isWeekly ? (
							<div className='overflow-x-auto'>
								<div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4'>
									{JORNADA_WEEK_DAYS.map((day) => {
										const selected =
											form.diasSemana.includes(day.value);
										const hours =
											form.diasSemanaHorarios?.[
												day.value
											] || {};
										return (
											<div
												key={day.value}
												className='group relative flex flex-col items-stretch gap-1'>
												<button
													type='button'
													onClick={() =>
														onToggleDay(day.value)
													}
													className={`h-[34px] w-full rounded-md border px-1 text-[13px] font-medium transition ${selected ? 'border-[#004A80] bg-[#004A80] text-white' : 'border-gray-200 bg-white text-[#8a8c8f] hover:bg-[#f8f7f7]'}`}>
													{day.label}
												</button>
												<HourRangeControl
													minWidthClassName='w-full min-w-0'
													marginTopClassName='mt-0'
													disabled={!selected}
													horaInicio={
														hours.horaInicio ||
														form.horaInicio ||
														''
													}
													horaFim={
														hours.horaFim ||
														form.horaFim ||
														''
													}
													onChangeHoraInicio={(
														value,
													) =>
														onChangeDayTime(
															day.value,
															'horaInicio',
															value,
														)
													}
													onChangeHoraFim={(value) =>
														onChangeDayTime(
															day.value,
															'horaFim',
															value,
														)
													}
												/>
											</div>
										);
									})}
								</div>
							</div>
						) : (
							<div className='grid grid-cols-1 gap-2 sm:grid-cols-3'>
								<label className='text-[11px] font-medium text-[#13335A]'>
									Ciclo
									<input
										value={recurrenceLabel}
										readOnly
										className='mt-1 h-[28px] w-full cursor-not-allowed rounded-md border border-gray-200 bg-[#F3F4F6] px-2 text-[10px] font-normal text-[#667085]'
									/>
								</label>
								<label className='text-[11px] font-medium text-[#13335A]'>
									Hora inicial
									<input
										type='time'
										value={form.horaInicio || ''}
										onChange={(event) =>
											onChange(
												'horaInicio',
												event.target.value,
											)
										}
										className='mt-1 h-[28px] w-full rounded-md border border-gray-200 px-2 text-[10px] font-normal outline-none'
									/>
								</label>
								<label className='text-[11px] font-medium text-[#13335A]'>
									Hora final
									<input
										type='time'
										value={form.horaFim || ''}
										onChange={(event) =>
											onChange(
												'horaFim',
												event.target.value,
											)
										}
										className='mt-1 h-[28px] w-full rounded-md border border-gray-200 px-2 text-[10px] font-normal outline-none'
									/>
								</label>
							</div>
						)}
					</div>
					<div className='rounded-md border border-[#B8C7D9] bg-white px-2 py-2 shadow-sm'>
						<div className='mb-2 flex items-center justify-between'>
							<p className='text-[12px] font-medium text-[#13335A]'>
								Dias específicos
							</p>
							<button
								type='button'
								onClick={onAddSpecificDate}
								className='flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 text-[18px] leading-none text-[#004A80] shadow-sm transition hover:bg-gray-50'
								aria-label='Adicionar dia específico'>
								+
							</button>
						</div>
						{form.datasEspecificas.length ? (
							<div className='space-y-2'>
								{form.datasEspecificas.map((date, index) => (
									<div
										key={`${date}-${index}`}
										className='flex items-center gap-2'>
										<input
											type='date'
											value={date}
											onChange={(event) =>
												onChangeSpecificDate(
													index,
													event.target.value,
												)
											}
											className='h-[28px] min-w-0 flex-1 rounded-md border border-gray-200 px-2 text-[11px] outline-none'
										/>
										<button
											type='button'
											onClick={() =>
												onRemoveSpecificDate(index)
											}
											className='text-[11px] text-[#BD504E]'>
											Remover
										</button>
									</div>
								))}
							</div>
						) : (
							<p className='text-[11px] text-[#98A2B3]'>
								Sem datas específicas
							</p>
						)}
					</div>
				</div>
				</div>
				</div>
			</div>
		</div>
	);
}

function GerenciarProfissionaisMultiprofissional() {
	const [token, setToken] = useState('');
	const [user, setUser] = useState(null);
	const [establishment, setEstablishment] = useState('');
	const [units, setUnits] = useState([]);
	const [unitSearchTerm, setUnitSearchTerm] = useState('');
	const [showUnitSuggestions, setShowUnitSuggestions] = useState(false);
	const [sectorSearchTerm, setSectorSearchTerm] = useState('');
	const [showSectorSuggestions, setShowSectorSuggestions] = useState(false);
	const [categories, setCategories] = useState([]);
	const [regimes, setRegimes] = useState([]);
	const [sectors, setSectors] = useState([]);
	const [selectedSector, setSelectedSector] = useState('');
	const [vinculoTypes, setVinculoTypes] = useState([]);
	const [recurrenceOptions, setRecurrenceOptions] =
		useState(RECURRENCE_OPTIONS);
	const [modalCategories, setModalCategories] = useState([]);
	const [modalRegimes, setModalRegimes] = useState([]);
	const [search, setSearch] = useState('');
	const [loadingEnvironment, setLoadingEnvironment] = useState(false);
	const [appliedContext, setAppliedContext] = useState(null);
	const [ambiente, setAmbiente] = useState(null);
	const [selectedCell, setSelectedCell] = useState(null);
	const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
	const [openMiniTabs, setOpenMiniTabs] = useState(getDefaultMiniTabs);
	const [sideActionSubmitting, setSideActionSubmitting] = useState(false);
	const [absenceType, setAbsenceType] = useState('FALTA');
	const [absencePeriod, setAbsencePeriod] = useState({
		dataInicio: '',
		dataFim: '',
	});
	const [absenceObservation, setAbsenceObservation] = useState('');
	const [isDeletePanelOpen, setIsDeletePanelOpen] = useState(false);
	const [deleteScope, setDeleteScope] = useState('APENAS_DIA');
	const [deleteReason, setDeleteReason] = useState('');
	const [error, setError] = useState('');
	const [modalError, setModalError] = useState('');
	const [modalFieldErrors, setModalFieldErrors] = useState({});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [professionalCreationConfirmation, setProfessionalCreationConfirmation] =
		useState(null);
	const [professional, setProfessional] = useState(emptyProfessional);
	const [isVacancyModalOpen, setIsVacancyModalOpen] = useState(false);
	const [vacancy, setVacancy] = useState(emptyVacancy);
	const [submitting, setSubmitting] = useState(false);
	const [feedback, setFeedback] = useState('');
	const [editingLine, setEditingLine] = useState(null);
	const [editingScope, setEditingScope] = useState('cadastro');
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [isJornadaModalOpen, setIsJornadaModalOpen] = useState(false);
	const [jornadaLine, setJornadaLine] = useState(null);
	const [jornadaForm, setJornadaForm] = useState(null);
	const [jornadaNewSectorForm, setJornadaNewSectorForm] = useState(null);
	const [jornadaSectorLines, setJornadaSectorLines] = useState([]);
	const [jornadaSectorForms, setJornadaSectorForms] = useState({});
	const [isJornadaNewSectorOpen, setIsJornadaNewSectorOpen] = useState(false);
	const [jornadaError, setJornadaError] = useState('');
	const [jornadaSubmitting, setJornadaSubmitting] = useState(false);

	const canSelectUnit =
		isDeveloperUser(user || {}) ||
		String(user?.unidade || '').toLowerCase() === 'subhue' ||
		user?.is_subhue === true;
	const canManage = canManageEscalaMultiprofissional(user || {});

	const loadMedicalCatalogs = useCallback(
		async (nextEstablishment) => {
			if (!token) return;
			const [unitsResult, catalogsResult] = await Promise.all([
				requestFiltrosBuscaEscalaMultiprofissional({
					token,
					somenteComItens: true,
				}),
				requestFiltrosBuscaEscalaMultiprofissional({
					token,
					estabelecimento: nextEstablishment || undefined,
				}),
			]);
			if (!unitsResult?.error) setUnits(normalizeUnits(unitsResult));
			if (catalogsResult?.error) {
				setSectors([]);
				setCategories([]);
				setRegimes([]);
				setError(
					getErrorMessage(
						catalogsResult,
						'Não foi possível carregar os filtros de unidade e setor.',
					),
				);
				return;
			}
			setCategories(
				(catalogsResult.categorias || [])
					.map(normalizeOption)
					.filter((item) => item.value),
			);
			setRegimes(
				(catalogsResult.regimes || [])
					.map(normalizeOption)
					.filter((item) => item.value),
			);
			setSectors(
				sortSetoresPorAndar(getCatalogStructureSectors(catalogsResult))
					.map(normalizeCatalogSectorOption)
					.filter((item) => item.value),
			);
		},
		[token],
	);

	const loadVinculoTypes = useCallback(async () => {
		if (!token) return;
		const result = await requestVinculosEscalaMultiprofissional({ token });
		if (result?.error) {
			setError(
				getErrorMessage(
					result,
					'Não foi possível carregar os tipos de vínculo.',
				),
			);
			return;
		}
		setVinculoTypes(
			result.map(normalizeOption).filter((item) => item.value),
		);
	}, [token]);

	useEffect(() => {
		const rawUser = Cookies.get('user');
		if (!rawUser) {
			setError('Usuário não autenticado.');
			return;
		}
		try {
			const parsed = JSON.parse(rawUser);
			const canChooseUnit =
				isDeveloperUser(parsed) ||
				String(parsed?.unidade || '')
					.trim()
					.toLowerCase() === 'subhue' ||
				parsed?.is_subhue === true;
			setUser(parsed);
			setToken(parsed.token || '');
			setEstablishment(
				canChooseUnit
					? ''
					: String(
							parsed.cnes ||
								parsed.cnes_estabelecimento ||
								parsed.cnesEstabelecimento ||
								'',
						).trim(),
			);
		} catch (_error) {
			setError('Não foi possível ler os dados do usuário autenticado.');
		}
	}, []);

	useEffect(() => {
		if (token) loadMedicalCatalogs(establishment);
	}, [establishment, loadMedicalCatalogs, token]);
	useEffect(() => {
		if (token) loadVinculoTypes();
	}, [loadVinculoTypes, token]);
	useEffect(() => {
		if (canSelectUnit || !establishment) return;
		const selectedUnit = units.find(
			(unit) => String(unit.value) === String(establishment),
		);
		setUnitSearchTerm(
			selectedUnit?.label ||
				getText(
					user?.unidade_nome,
					user?.estabelecimento?.nome,
					user?.nome_unidade,
					'',
				),
		);
	}, [canSelectUnit, establishment, units, user]);

	const loadEnvironment = useCallback(
		async ({
			nextEstablishment,
			nextSector,
			mesReferencia,
			clearOnError = false,
			minimumLoadingTime = 2000,
		}) => {
			const loadingStartedAt = Date.now();
			setLoadingEnvironment(true);
			setError('');
			setSelectedCell(null);
			const result = await requestAbrirAmbienteEscalaMultiprofissional({
				token,
				estabelecimento: nextEstablishment,
				setor: isAllSectorsValue(nextSector) ? '' : nextSector,
				mesReferencia,
				modo: 'detalhado',
			});
			if (result?.error) {
				setError(
					getErrorMessage(
						result,
						'Não foi possível carregar a escala deste setor.',
					),
				);
				if (clearOnError) {
					setAmbiente(null);
					setAppliedContext(null);
				}
			} else {
				setAmbiente(result?.ambiente || result);
				setAppliedContext({
					estabelecimento: nextEstablishment,
					setor: nextSector,
					mesReferencia,
				});
			}
			await waitForMinimumLoadingTime(
				loadingStartedAt,
				minimumLoadingTime,
			);
			setLoadingEnvironment(false);
		},
		[token],
	);

	const handleSelectContext = async () => {
		if (!establishment || !selectedSector) {
			setError('Selecione uma unidade e um setor para continuar.');
			return;
		}
		await loadEnvironment({
			nextEstablishment: establishment,
			nextSector: selectedSector,
			mesReferencia: getMonthReference(),
			clearOnError: true,
		});
	};

	const filteredUnits = useMemo(() => {
		const term = normalizeSearch(unitSearchTerm);
		if (!term) return units;
		return units.filter(
			(unit) =>
				normalizeSearch(unit.label).includes(term) ||
				normalizeSearch(unit.value).includes(term),
		);
	}, [unitSearchTerm, units]);
	const sectorsWithAll = useMemo(
		() => (sectors.length ? [ALL_SECTORS_OPTION, ...sectors] : sectors),
		[sectors],
	);
	const availableRegimes = useMemo(
		() =>
			regimes.length
				? regimes
				: [
						{ value: 'ROTINA', label: 'Rotina' },
						{ value: 'PLANTONISTA', label: 'Plantonista' },
					],
		[regimes],
	);
	const selectedRecurrence = useMemo(
		() =>
			recurrenceOptions.find(
				(item) => item.value === professional.recorrencia,
			) || RECURRENCE_OPTIONS[0],
		[professional.recorrencia, recurrenceOptions],
	);

	const handleUnitInputChange = (value) => {
		setUnitSearchTerm(value);
		setShowUnitSuggestions(true);
		setShowSectorSuggestions(false);
		const normalizedValue = normalizeSearch(value);
		const matchingUnit = units.find(
			(unit) =>
				normalizeSearch(unit.label) === normalizedValue ||
				normalizeSearch(unit.value) === normalizedValue,
		);
		if (matchingUnit) {
			setEstablishment(matchingUnit.value);
		} else {
			setEstablishment('');
			setSelectedSector('');
			setSectorSearchTerm('');
			setAppliedContext(null);
			setAmbiente(null);
		}
	};

	const handleSelectUnit = (unit) => {
		setUnitSearchTerm(unit.label);
		setShowUnitSuggestions(false);
		setEstablishment(unit.value);
		setSelectedSector('');
		setSectorSearchTerm('');
		setAppliedContext(null);
		setAmbiente(null);
	};

	const handleSectorInputChange = (value) => {
		if (!establishment) return;
		setSectorSearchTerm(value);
		setShowSectorSuggestions(true);
		setShowUnitSuggestions(false);
		const normalizedValue = normalizeSearch(value);
		const matchingSector = sectorsWithAll.find(
			(sector) =>
				normalizeSearch(sector.label) === normalizedValue ||
				normalizeSearch(sector.identificador) === normalizedValue ||
				normalizeSearch(sector.value) === normalizedValue,
		);
		setSelectedSector(matchingSector?.value || '');
		if (!matchingSector) {
			setAppliedContext(null);
			setAmbiente(null);
		}
	};

	const handleSelectSector = (sector) => {
		setSectorSearchTerm(sector.label);
		setSelectedSector(sector.value);
		setShowSectorSuggestions(false);
		setAppliedContext(null);
		setAmbiente(null);
	};

	const calendarDays = useMemo(
		() => getMonthDays(ambiente?.calendario || []),
		[ambiente],
	);
	const environmentRows = useMemo(() => {
		const lines = Array.isArray(ambiente?.linhas) ? ambiente.linhas : [];
		return isAllSectorsValue(appliedContext?.setor)
			? consolidateProfessionalLines(lines)
			: lines;
	}, [ambiente, appliedContext?.setor]);
	const visibleRows = useMemo(() => {
		const term = String(search || '')
			.trim()
			.toLowerCase();
		if (!term) return environmentRows;
		return environmentRows.filter((linha) =>
			getLineName(linha).toLowerCase().includes(term),
		);
	}, [environmentRows, search]);
	const rowsToRender = useMemo(() => {
		const rows = visibleRows.map((linha) => ({
			...linha,
			placeholder: false,
		}));
		const missingRows = Math.max(0, MINIMUM_CALENDAR_ROWS - rows.length);
		return rows.concat(
			Array.from({ length: missingRows }, (_, index) => ({
				linha_id: `placeholder-${index}`,
				placeholder: true,
			})),
		);
	}, [visibleRows]);
	const calendarGridStyle = useMemo(
		() => ({
			gridTemplateColumns: `repeat(${Math.max(calendarDays.length, 1)}, minmax(34px, 1fr))`,
		}),
		[calendarDays.length],
	);
	const monthLabel = useMemo(() => {
		const reference = toIsoDate(
			ambiente?.contexto?.mes_referencia || calendarDays[0]?.data,
		);
		if (!reference) return '';
		return new Date(`${reference}T12:00:00`).toLocaleDateString('pt-BR', {
			month: 'long',
			year: 'numeric',
		});
	}, [ambiente, calendarDays]);
	const sectorLabel = useMemo(
		() =>
			isAllSectorsValue(appliedContext?.setor)
				? 'Todos os setores'
				: sectors.find(
						(item) =>
							String(item.value) ===
							String(appliedContext?.setor),
					)?.label || '',
		[appliedContext, sectors],
	);
	const todayIsoDate = useMemo(getCurrentIsoDate, []);
	const changeCalendarMonth = async (offset) => {
		if (!appliedContext || loadingEnvironment) return;
		const reference =
			toIsoDate(
				ambiente?.contexto?.mes_referencia || calendarDays[0]?.data,
			) || getMonthReference();
		const date = new Date(`${reference}T12:00:00`);
		date.setMonth(date.getMonth() + offset);
		await loadEnvironment({
			nextEstablishment: appliedContext.estabelecimento,
			nextSector: appliedContext.setor,
			mesReferencia: getMonthReference(date),
			minimumLoadingTime: 1000,
		});
	};
	const goToCurrentMonth = async () => {
		if (!appliedContext || loadingEnvironment) return;
		await loadEnvironment({
			nextEstablishment: appliedContext.estabelecimento,
			nextSector: appliedContext.setor,
			mesReferencia: getMonthReference(),
			minimumLoadingTime: 1000,
		});
	};

	const getOperationalSector = (sector) =>
		isAllSectorsValue(sector) ? '' : String(sector || '');
	const getDefaultOperationalValues = () => ({
		setor: getOperationalSector(appliedContext?.setor),
		data:
			toIsoDate(
				appliedContext?.mesReferencia ||
					ambiente?.contexto?.mes_referencia,
			) || getMonthReference(),
	});
	const buildRegimeBasePayload = ({ form, startDate }) => {
		const option =
			recurrenceOptions.find((item) => item.value === form.recorrencia) ||
			RECURRENCE_OPTIONS[0];
		const specificSchedules = (form.datasEspecificas || [])
			.map((date, index) => ({
				data: toIsoDate(date),
				hora_inicio:
					form.datasEspecificasHorarios?.[index]?.horaInicio ||
					form.horaInicio ||
					'',
				hora_fim:
					form.datasEspecificasHorarios?.[index]?.horaFim ||
					form.horaFim ||
					'',
			}))
			.filter((item) => item.data);
		const firstWeeklySchedule =
			form.diasSemana
				.map((diaSemana) => form.diasSemanaHorarios?.[diaSemana] || {})
				.find((schedule) => schedule.horaInicio && schedule.horaFim) ||
			{};
		const base = {
			regime: form.regime,
			tipo_recorrencia: option.tipo_recorrencia,
			preset_ciclo: option.preset_ciclo,
			vigencia_inicio: startDate,
			data_ancora: startDate,
			hora_inicio:
				form.horaInicio ||
				firstWeeklySchedule.horaInicio ||
				specificSchedules[0]?.hora_inicio ||
				'',
			hora_fim:
				form.horaFim ||
				firstWeeklySchedule.horaFim ||
				specificSchedules[0]?.hora_fim ||
				'',
		};
		if (option.tipo_recorrencia === 'SEMANAL')
			return {
				...base,
				dias_semana: form.diasSemana,
				ocorrencias: form.diasSemana.map((diaSemana) => ({
					dia_semana: diaSemana,
					hora_inicio:
						form.diasSemanaHorarios?.[diaSemana]?.horaInicio ||
						form.horaInicio,
					hora_fim:
						form.diasSemanaHorarios?.[diaSemana]?.horaFim ||
						form.horaFim,
				})),
				padroes_alocacao: form.diasSemana.map((diaSemana) => ({
					tipo_recorrencia: 'SEMANAL',
					preset_ciclo: 'SEMANAL',
					dias_semana: [diaSemana],
					hora_inicio:
						form.diasSemanaHorarios?.[diaSemana]?.horaInicio ||
						form.horaInicio,
					hora_fim:
						form.diasSemanaHorarios?.[diaSemana]?.horaFim ||
						form.horaFim,
				})),
			};
		if (option.tipo_recorrencia === 'ROTACAO_PERIODICA')
			return {
				...base,
				periodo_semanas: form.semanasRotacao?.length || 2,
				semanas_rotacao: (form.semanasRotacao || []).map(
					(diasSemana, index) => ({
						semana: index + 1,
						dias_semana: diasSemana,
					}),
				),
			};
		if (option.tipo_recorrencia === 'DATAS_ESPECIFICAS')
			return {
				...base,
				datas_especificas: specificSchedules.map((item) => item.data),
				padroes_alocacao: specificSchedules.map((item) => ({
					tipo_recorrencia: 'DATAS_ESPECIFICAS',
					preset_ciclo: option.preset_ciclo || 'PERSONALIZADO',
					datas_especificas: [item.data],
					hora_inicio: item.hora_inicio,
					hora_fim: item.hora_fim,
				})),
			};
		return base;
	};
	const buildRegimePayload = ({ form, startDate }) => {
		const base = buildRegimeBasePayload({ form, startDate });
		if (
			base.tipo_recorrencia === 'DATAS_ESPECIFICAS' ||
			!form.datasEspecificas?.length
		)
			return base;
		const extras = (form.datasEspecificas || [])
			.map((data, index) => ({
				tipo_recorrencia: 'DATAS_ESPECIFICAS',
				preset_ciclo: 'PERSONALIZADO',
				datas_especificas: [data],
				hora_inicio:
					form.datasEspecificasHorarios?.[index]?.horaInicio ||
					form.horaInicio,
				hora_fim:
					form.datasEspecificasHorarios?.[index]?.horaFim ||
					form.horaFim,
			}))
			.filter((item) => item.datas_especificas[0]);
		return {
			...base,
			padroes_alocacao: [
				...(base.padroes_alocacao || [{ ...base }]),
				...extras,
			],
		};
	};
	const loadCreationOptions = async (selectedModalSector) => {
		if (!appliedContext) return;
		const operationalSector = String(
			selectedModalSector ?? getOperationalSector(appliedContext.setor),
		);
		const [result, catalogResult] = await Promise.all([
			operationalSector
				? requestOpcoesAmbientePreenchimentoMultiprofissional({
						token,
						contexto: {
							estabelecimento: appliedContext.estabelecimento,
							setor: operationalSector,
							mes_referencia: appliedContext.mesReferencia,
						},
					})
				: Promise.resolve(null),
			requestCatalogosEscalaSelectsMultiprofissional({
				token,
				estabelecimento: appliedContext.estabelecimento,
			}),
		]);
		if (!result?.error)
			setRecurrenceOptions(getRecurrenceOptionsFromPayload(result));
		if (!catalogResult?.error) {
			setModalCategories(
				(
					catalogResult.categorias ||
					catalogResult.categorias_multiprofissional ||
					[]
				)
					.map(normalizeOption)
					.filter((item) => item.value),
			);
			setModalRegimes(
				(catalogResult.regimes || [])
					.map(normalizeOption)
					.filter((item) => item.value),
			);
		}
	};
	const openProfessionalModal = () => {
		const values = getDefaultOperationalValues();
		setFeedback('');
		setError('');
		setModalError('');
		setModalFieldErrors({});
		setProfessionalCreationConfirmation(null);
		setEditingLine(null);
		setEditingScope('cadastro');
		setProfessional({
			...emptyProfessional(),
			setor: values.setor,
			vigenciaInicio: getCurrentIsoDate(),
			admissao: values.data,
		});
		setIsModalOpen(true);
		loadCreationOptions(values.setor);
	};
	const getJornadaFormFromLine = (linha) => {
		const values = getDefaultOperationalValues();
		const details = getLineProfessionalDetails(linha);
		const regimeEscala = linha.regime_escala || linha.regimeEscala || {};
		const padrao = Array.isArray(regimeEscala.padroes_alocacao)
			? regimeEscala.padroes_alocacao[0] || {}
			: {};
		const recurrenceType = String(
			padrao.tipo_recorrencia ||
				regimeEscala.tipo_recorrencia ||
				'SEMANAL',
		).toUpperCase();
		const recurrencePreset = String(
			padrao.preset_ciclo || regimeEscala.preset_ciclo || '',
		)
			.toUpperCase()
			.replace(/^CICLO_/, '');
		const recurrence =
			recurrenceType === 'SEMANAL'
				? 'SEMANAL'
				: recurrenceType === 'ROTACAO_PERIODICA'
					? 'ROTACAO_PERIODICA'
					: recurrenceType === 'DATAS_ESPECIFICAS'
						? 'DATAS_ESPECIFICAS'
						: `CICLO_${recurrencePreset.replace(/[^A-Z0-9]/g, '_')}`;
		return {
			...emptyProfessional(),
			cpf: formatCpf(details.cpf),
			nome: details.nome || getLineName(linha),
			setor: String(details.setor || values.setor || ''),
			categoria: String(details.categoria || ''),
			vinculoTipo: String(details.vinculoTipo || ''),
			admissao: toIsoDate(details.admissao) || values.data,
			cargo: String(details.cargo || ''),
			numeroConselho: String(details.numeroConselho || ''),
			ufConselho: String(details.ufConselho || 'RJ'),
			telefone: formatPhone(details.telefone),
			regime: String(regimeEscala.regime || 'ROTINA'),
			recorrencia: recurrence,
			vigenciaInicio:
				toIsoDate(
					regimeEscala.vigencia_inicio || regimeEscala.data_ancora,
				) || values.data,
			horaInicio: String(
				padrao.hora_inicio || regimeEscala.hora_inicio || '07:00',
			).slice(0, 5),
			horaFim: String(
				padrao.hora_fim || regimeEscala.hora_fim || '19:00',
			).slice(0, 5),
			diasSemana: Array.isArray(padrao.dias_semana)
				? padrao.dias_semana
				: Array.isArray(regimeEscala.dias_semana)
					? regimeEscala.dias_semana
					: [1, 2, 3, 4, 5],
			diasSemanaHorarios: (regimeEscala.ocorrencias || []).reduce(
				(result, occurrence) => ({
					...result,
					[occurrence.dia_semana]: {
						horaInicio: String(occurrence.hora_inicio || '').slice(
							0,
							5,
						),
						horaFim: String(occurrence.hora_fim || '').slice(0, 5),
					},
				}),
				{},
			),
			semanasRotacao: (
				padrao.semanas_rotacao ||
				regimeEscala.semanas_rotacao || [
					[1, 2, 3, 4, 5],
					[1, 2, 3, 4, 5],
				]
			).map((week) =>
				Array.isArray(week?.dias_semana) ? week.dias_semana : week,
			),
			datasEspecificas: (
				padrao.datas_especificas ||
				regimeEscala.datas_especificas ||
				[]
			)
				.map(toIsoDate)
				.filter(Boolean),
		};
	};
	const openJornadaModal = async (linha) => {
		if (!linha || linha.placeholder || isVacancyLine(linha)) return;
		if (!appliedContext || !getLineVinculoId(linha)) {
			setError(
				'Não foi possível identificar o vínculo desta linha para carregar os turnos.',
			);
			return;
		}
		const result = await requestAbrirAmbienteEscalaMultiprofissional({
			token,
			estabelecimento: appliedContext.estabelecimento,
			mesReferencia: appliedContext.mesReferencia,
			modo: 'detalhado',
			vinculoProfissionalId: getLineVinculoId(linha),
		});
		if (result?.error) {
			setError(
				getErrorMessage(result, 'Não foi possível carregar os turnos.'),
			);
			return;
		}
		const linhasDetalhadas = (result?.ambiente || result)?.linhas || [];
		const vinculoId = getLineVinculoId(linha);
		const linhasDoVinculo = linhasDetalhadas.filter(
			(item) => String(getLineVinculoId(item)) === String(vinculoId),
		);
		const detalhe = linhasDoVinculo.find(
			(item) =>
				String(getLineRegimeVinculoId(item)) ===
				String(getLineRegimeVinculoId(linha)),
		);
		if (!detalhe) {
			setError(
				'Não foi possível identificar a linha selecionada no detalhe da escala.',
			);
			return;
		}
		linha = { ...linha, ...detalhe };
		const form = getJornadaFormFromLine(linha);
		setJornadaSectorLines(linhasDoVinculo.length ? linhasDoVinculo : [linha]);
		setJornadaSectorForms(
			Object.fromEntries(
				(linhasDoVinculo.length ? linhasDoVinculo : [linha]).map((sectorLine) => [
					String(getLineRegimeVinculoId(sectorLine)),
					getJornadaFormFromLine(sectorLine),
				]),
			),
		);
		setJornadaLine(linha);
		setJornadaForm(form);
		setJornadaNewSectorForm({ ...form, setor: '' });
		setIsJornadaNewSectorOpen(false);
		setJornadaError('');
		setIsJornadaModalOpen(true);
		loadCreationOptions();
	};
	const closeJornadaModal = () => {
		setIsJornadaModalOpen(false);
		setJornadaLine(null);
		setJornadaForm(null);
		setJornadaNewSectorForm(null);
		setJornadaSectorLines([]);
		setJornadaSectorForms({});
		setIsJornadaNewSectorOpen(false);
		setJornadaError('');
	};
	const openEditProfessionalModal = async (linha, scope = 'cadastro') => {
		if (!linha || linha.placeholder || isVacancyLine(linha)) return;
		if (scope === 'jornada') {
			openJornadaModal(linha);
			return;
		}
		const values = getDefaultOperationalValues();
		let details = getLineProfessionalDetails(linha);
		const vinculoId = getLineVinculoId(linha);
		if (vinculoId) {
			setSubmitting(true);
			const cadastro = await requestDetalharVinculoMultiprofissional({
				token,
				id: vinculoId,
			});
			const origens = await requestContextosOrigemMultiprofissional({
				token,
				estabelecimento: appliedContext.estabelecimento,
				mesReferencia: appliedContext.mesReferencia,
				vinculoProfissionalId: vinculoId,
			});
			const contextos = Array.isArray(origens?.contextos_operacionais)
				? origens.contextos_operacionais
				: [origens?.contextos_operacionais].filter(Boolean);
			const contextoLinha =
				contextos
					.map((item) => item.linha)
					.find(
						(item) =>
							String(getLineRegimeVinculoId(item)) ===
							String(getLineRegimeVinculoId(linha)),
					) || linha;
			const detalhe = await requestDetalharLinhaMultiprofissional({
				token,
				estabelecimento: appliedContext.estabelecimento,
				mesReferencia: appliedContext.mesReferencia,
				setor: contextoLinha?.setor?.id || linha?.setor?.id,
				regimeVinculoId: getLineRegimeVinculoId(contextoLinha),
			});
			setSubmitting(false);
			const falha = [cadastro, origens, detalhe].find(
				(result) => result?.error,
			);
			if (falha) {
				setError(
					getErrorMessage(
						falha,
						'Não foi possível carregar os dados para edição.',
					),
				);
				return;
			}
			linha = { ...linha, ...detalhe?.linha };
			const detalhesLinha = getLineProfessionalDetails(linha);
			const detalhesCadastro = getLineProfessionalDetails({
				...cadastro,
				vinculo_profissional: cadastro,
				cpf: cadastro?.cpf_profissional,
				vinculo_tipo_id: cadastro?.tipo_vinculo_id,
			});
			details = Object.fromEntries(
				Object.keys(detalhesLinha).map((key) => [
					key,
					detalhesCadastro[key] || detalhesLinha[key],
				]),
			);
		}
		const regimeEscala = linha.regime_escala || linha.regimeEscala || {};
		const padrao = Array.isArray(regimeEscala.padroes_alocacao)
			? regimeEscala.padroes_alocacao[0] || {}
			: {};
		const recurrenceType = String(
			padrao.tipo_recorrencia ||
				regimeEscala.tipo_recorrencia ||
				'SEMANAL',
		).toUpperCase();
		const recurrencePreset = String(
			padrao.preset_ciclo || regimeEscala.preset_ciclo || '',
		)
			.toUpperCase()
			.replace(/^CICLO_/, '');
		const recurrence =
			recurrenceType === 'SEMANAL'
				? 'SEMANAL'
				: recurrenceType === 'ROTACAO_PERIODICA'
					? 'ROTACAO_PERIODICA'
					: recurrenceType === 'DATAS_ESPECIFICAS'
						? 'DATAS_ESPECIFICAS'
						: `CICLO_${recurrencePreset.replace(/[^A-Z0-9]/g, '_')}`;
		const setorLinha = details.setor || values.setor;
		setFeedback('');
		setError('');
		setModalError('');
		setModalFieldErrors({});
		setEditingLine(linha);
		setEditingScope(scope);
		setProfessional({
			...emptyProfessional(),
			cpf: formatCpf(details.cpf),
			nome: details.nome || getLineName(linha),
			setor: String(setorLinha || ''),
			categoria: String(details.categoria || ''),
			vinculoTipo: String(details.vinculoTipo || ''),
			admissao: toIsoDate(details.admissao) || values.data,
			cargo: String(details.cargo || ''),
			numeroConselho: String(details.numeroConselho || ''),
			ufConselho: String(details.ufConselho || 'RJ'),
			telefone: formatPhone(details.telefone),
			regime: String(regimeEscala.regime || 'ROTINA'),
			recorrencia: recurrence,
			vigenciaInicio:
				toIsoDate(
					regimeEscala.vigencia_inicio || regimeEscala.data_ancora,
				) || values.data,
			horaInicio: String(
				padrao.hora_inicio || regimeEscala.hora_inicio || '07:00',
			).slice(0, 5),
			horaFim: String(
				padrao.hora_fim || regimeEscala.hora_fim || '19:00',
			).slice(0, 5),
			diasSemana: Array.isArray(padrao.dias_semana)
				? padrao.dias_semana
				: Array.isArray(regimeEscala.dias_semana)
					? regimeEscala.dias_semana
					: [1, 2, 3, 4, 5],
			diasSemanaHorarios: (regimeEscala.ocorrencias || []).reduce(
				(result, occurrence) => ({
					...result,
					[occurrence.dia_semana]: {
						horaInicio: String(occurrence.hora_inicio || '').slice(
							0,
							5,
						),
						horaFim: String(occurrence.hora_fim || '').slice(0, 5),
					},
				}),
				{},
			),
			semanasRotacao: (
				padrao.semanas_rotacao ||
				regimeEscala.semanas_rotacao || [
					[1, 2, 3, 4, 5],
					[1, 2, 3, 4, 5],
				]
			).map((week) =>
				Array.isArray(week?.dias_semana) ? week.dias_semana : week,
			),
			datasEspecificas: (
				padrao.datas_especificas ||
				regimeEscala.datas_especificas ||
				[]
			)
				.map(toIsoDate)
				.filter(Boolean),
		});
		setIsModalOpen(true);
		loadCreationOptions();
	};
	const closeProfessionalModal = () => {
		setIsModalOpen(false);
		setModalError('');
		setModalFieldErrors({});
		setProfessionalCreationConfirmation(null);
		setEditingLine(null);
		setEditingScope('cadastro');
	};
	const openVacancyModal = () => {
		const values = getDefaultOperationalValues();
		setFeedback('');
		setError('');
		setVacancy({
			...emptyVacancy(),
			setor: values.setor,
			dataInicio: values.data,
		});
		setIsVacancyModalOpen(true);
	};
	const toggleProfessionalWeekDay = (day) =>
		setProfessional((current) => ({
			...current,
			diasSemana: current.diasSemana.includes(day)
				? current.diasSemana.filter((item) => item !== day)
				: [...current.diasSemana, day].sort(
						(first, second) => first - second,
					),
		}));
	const updateProfessionalWeekDayTime = (day, field, value) =>
		setProfessional((current) => ({
			...current,
			diasSemanaHorarios: {
				...current.diasSemanaHorarios,
				[day]: {
					...(current.diasSemanaHorarios?.[day] || {
						horaInicio: current.horaInicio,
						horaFim: current.horaFim,
					}),
					[field]: value,
				},
			},
		}));
	const toggleProfessionalRotationWeekDay = (weekIndex, day) =>
		setProfessional((current) => ({
			...current,
			semanasRotacao: (current.semanasRotacao || []).map((week, index) =>
				index !== weekIndex
					? week
					: week.includes(day)
						? week.filter((item) => item !== day)
						: [...week, day].sort(
								(first, second) => first - second,
							),
			),
		}));
	const addSpecificDate = () =>
		setProfessional((current) => ({
			...current,
			datasEspecificas: [
				...current.datasEspecificas,
				current.vigenciaInicio || '',
			],
		}));
	const updateSpecificDate = (index, value) => {
		const safeValue =
			!editingLine && value < getCurrentIsoDate()
				? getCurrentIsoDate()
				: value;
		return setProfessional((current) => ({
			...current,
			datasEspecificas: current.datasEspecificas.map((date, dateIndex) =>
				dateIndex === index ? safeValue : date,
			),
		}));
	};
	const removeSpecificDate = (index) =>
		setProfessional((current) => ({
			...current,
			datasEspecificasHorarios: (
				current.datasEspecificasHorarios || []
			).filter((_, dateIndex) => dateIndex !== index),
			datasEspecificas: current.datasEspecificas.filter(
				(_, dateIndex) => dateIndex !== index,
			),
		}));
	const clearModalFieldError = (...fields) => {
		if (!fields.length) return;
		setModalFieldErrors((current) => {
			const next = { ...current };
			fields.flat().forEach((field) => delete next[field]);
			return next;
		});
	};
	const getProfessionalFieldErrors = ({
		recurrence = selectedRecurrence,
	} = {}) => {
		const next = {};
		const isWeekly = recurrence?.tipo_recorrencia === 'SEMANAL';
		const isSpecificDates =
			recurrence?.tipo_recorrencia === 'DATAS_ESPECIFICAS';
		const isRotation = recurrence?.tipo_recorrencia === 'ROTACAO_PERIODICA';
		if (getCpfDigits(professional.cpf).length !== 11) next.cpf = true;
		if (!String(professional.nome || '').trim()) next.nome = true;
		if (!professional.setor) next.setor = true;
		if (!professional.admissao) next.admissao = true;
		if (!professional.vinculoTipo) next.vinculoTipo = true;
		if (!professional.categoria) next.categoria = true;
		if (editingLine && editingScope !== 'cadastro-escala') return next;
		if (!professional.regime) next.regime = true;
		if (!professional.recorrencia) next.recorrencia = true;
		if (!professional.vigenciaInicio) next.vigenciaInicio = true;
		if (isWeekly) {
			if (!professional.diasSemana.length) next.diasSemana = true;
			if (
				professional.diasSemana.some((diaSemana) => {
					const schedule =
						professional.diasSemanaHorarios?.[diaSemana] || {};
					return (
						!(schedule.horaInicio || professional.horaInicio) ||
						!(schedule.horaFim || professional.horaFim)
					);
				})
			)
				next.horarios = true;
		}
		if (professional.datasEspecificas.some((data) => !data))
			next.datasEspecificas = true;
		if (
			professional.datasEspecificas.some((data, index) => {
				const horario =
					professional.datasEspecificasHorarios?.[index] || {};
				return (
					!(horario.horaInicio || professional.horaInicio) ||
					!(horario.horaFim || professional.horaFim)
				);
			})
		)
			next.horarios = true;
		if (isSpecificDates) {
			if (!professional.datasEspecificas.length)
				next.datasEspecificas = true;
		}
		if (
			!isWeekly &&
			!isSpecificDates &&
			!isRotation &&
			(!professional.horaInicio || !professional.horaFim)
		)
			next.horarios = true;
		if (
			isRotation &&
			!(professional.semanasRotacao || []).some((week) => week?.length)
		)
			next.diasSemana = true;
		return next;
	};
	const getProfessionalValidationMessage = (fieldErrors) => {
		const fields = Object.keys(fieldErrors);
		if (fields.length > 1)
			return 'Preencha os campos obrigatórios destacados.';
		if (fieldErrors.cpf) return 'Informe o CPF completo com 11 dígitos.';
		if (fieldErrors.horarios) return 'Informe os horários da jornada.';
		if (fieldErrors.diasSemana)
			return 'Selecione ao menos um dia da jornada.';
		if (fieldErrors.datasEspecificas)
			return 'Informe ao menos uma data específica para a jornada.';
		return 'Preencha o campo obrigatório destacado.';
	};
	const searchProfessionalByCpf = async () => {
		const cpf = getCpfDigits(professional.cpf);
		if (cpf.length !== 11) {
			setModalFieldErrors((current) => ({ ...current, cpf: true }));
			setModalError('Informe o CPF completo com 11 dígitos.');
			return;
		}
		setSubmitting(true);
		setModalError('');
		const result = await requestBuscarProfissionalEscalaMultiprofissional({
			token,
			cpf,
			estabelecimento: establishment || appliedContext?.estabelecimento,
		});
		if (result?.error)
			setModalError(
				getErrorMessage(
					result,
					'Não foi possível consultar o profissional.',
				),
			);
		else {
			const found = Array.isArray(result)
				? result[0]
				: result?.results?.[0];
			if (found)
				setProfessional((current) => ({
					...current,
					nome: getText(
						found.nome,
						found.profissional?.nome,
						current.nome,
					),
					categoria: String(
						found.categoria?.id ||
							found.vinculo_profissional?.categoria?.id ||
							current.categoria ||
							'',
					),
					vinculoTipo: String(
						found.vinculo_tipo?.id ||
							found.vinculo_profissional?.vinculo_tipo?.id ||
							current.vinculoTipo ||
							'',
					),
				}));
		}
		setSubmitting(false);
	};
	const getProfessionalCreationImpact = async (cpf, targetSector) => {
		const found = await requestBuscarProfissionalEscalaMultiprofissional({
			token,
			cpf,
			estabelecimento: appliedContext?.estabelecimento,
		});
		if (found?.error)
			return { sectors: [], verificationUnavailable: true };

		const vinculIds = Array.from(
			new Set(
				(found || []).flatMap((item) => getSearchResultVinculoIds(item)),
			),
		);
		if (!vinculIds.length)
			return {
				sectors: [],
				verificationUnavailable: Array.isArray(found) && found.length > 0,
			};

		const contextResults = await Promise.all(
			vinculIds.map((vinculoProfissionalId) =>
				requestContextosOrigemMultiprofissional({
					token,
					estabelecimento: appliedContext?.estabelecimento,
					mesReferencia: appliedContext?.mesReferencia,
					vinculoProfissionalId,
				}),
			),
		);
		const verificationUnavailable = contextResults.some(
			(result) => result?.error,
		);
		const sectorsById = new Map();
		contextResults
			.filter((result) => !result?.error)
			.flatMap(getContextLines)
			.forEach((line) => {
				const sectorId = String(line?.setor?.id || line?.setor_id || '');
				if (!sectorId || sectorId === String(targetSector)) return;
				sectorsById.set(
					sectorId,
					getText(
						line?.setor?.nome_exibicao,
						line?.setor?.nome,
						sectors.find((sector) => String(sector.value) === sectorId)
							?.label,
						sectorId,
					),
				);
			});

		return {
			sectors: Array.from(sectorsById.values()),
			verificationUnavailable,
		};
	};
	const confirmProfessionalCreation = async () => {
		const pending = professionalCreationConfirmation;
		if (!pending) return;
		setSubmitting(true);
		setModalError('');
		const result = await requestSalvarProfissionalAmbienteMultiprofissional({
			token,
			body: pending.body,
		});
		if (result?.error)
			setModalError(
				getErrorMessage(
					result,
					'Não foi possível adicionar o profissional à escala.',
				),
			);
		else {
			setFeedback(
				pending.kind === 'jornada-new-sector'
					? 'Profissional alocado no novo setor.'
					: 'Profissional adicionado à escala multiprofissional.',
			);
			if (pending.kind === 'jornada-new-sector') {
				setProfessionalCreationConfirmation(null);
				closeJornadaModal();
			} else closeProfessionalModal();
			await loadEnvironment({
				nextEstablishment: appliedContext.estabelecimento,
				nextSector: appliedContext.setor,
				mesReferencia: appliedContext.mesReferencia,
				minimumLoadingTime: 1000,
			});
		}
		setSubmitting(false);
	};
	const submitProfessional = async (event) => {
		event.preventDefault();
		const cpf = getCpfDigits(professional.cpf);
		const fieldErrors = getProfessionalFieldErrors();
		if (!appliedContext || Object.keys(fieldErrors).length) {
			setModalFieldErrors(fieldErrors);
			setModalError(
				!appliedContext
					? 'Selecione a unidade e o setor antes de adicionar um profissional.'
					: getProfessionalValidationMessage(fieldErrors),
			);
			return;
		}
		setSubmitting(true);
		setModalError('');
		setModalFieldErrors({});
		const regimeEscala = {
			...buildRegimePayload({
				form: professional,
				startDate: professional.vigenciaInicio,
			}),
			categoria_id: Number(professional.categoria),
		};
		const body = {
			contexto: {
				estabelecimento: appliedContext.estabelecimento,
				setor: Number(professional.setor),
				mes_referencia: appliedContext.mesReferencia,
			},
			cpf,
			profissional: {
				nome: professional.nome.trim(),
				numero_registro: professional.numeroConselho || undefined,
				uf_conselho: professional.ufConselho || undefined,
				telefone: professional.telefone.replace(/\D/g, '') || undefined,
			},
			vinculo_profissional: {
				vinculo_tipo_id: Number(professional.vinculoTipo),
				categoria_id: Number(professional.categoria),
				data_admissao: professional.admissao || undefined,
				cargo: professional.cargo || undefined,
			},
			regime_escala: regimeEscala,
		};
		if (!editingLine) {
			const impact = await getProfessionalCreationImpact(
				cpf,
				professional.setor,
			);
			setProfessionalCreationConfirmation({
				kind: 'professional',
				body,
				...impact,
			});
			setSubmitting(false);
			return;
		}
		const vinculoId = getLineVinculoId(editingLine);
		let result;
		if (editingLine && vinculoId) {
			result = await requestAtualizarVinculoProfissionalMultiprofissional(
				{
					token,
					id: vinculoId,
					body: {
						tipo_vinculo_id: Number(professional.vinculoTipo),
						categoria: Number(professional.categoria),
						data_admissao: professional.admissao || null,
						numero_registro:
							professional.numeroConselho || undefined,
						uf_conselho: professional.ufConselho || undefined,
						telefone:
							professional.telefone.replace(/\D/g, '') ||
							undefined,
							cargo: professional.cargo || undefined,
							...(editingScope === 'cadastro-escala' && {
								regime_escala: {
									...regimeEscala,
									regime_vinculo_id: Number(
										getLineRegimeVinculoId(editingLine),
									),
								},
							}),
							chave_idempotencia: createIdempotencyKey(
							'editar-vinculo-multiprofissional',
						),
					},
				},
			);
		} else {
			// Para uma jornada nova, o ambiente assistido resolve o profissional,
			// cria/atualiza o vínculo e grava a alocação no mesmo comando. Criar o
			// vínculo separadamente e montar `alocacao-origem/prever` no cliente
			// pula regras internas do fluxo e foi a origem do 500 registrado no HAR.
			result = await requestSalvarProfissionalAmbienteMultiprofissional({
				token,
				body,
			});
		}
		if (result?.error)
			setModalError(
				getErrorMessage(
					result,
					editingLine
						? 'Não foi possível salvar as alterações do profissional.'
						: 'Não foi possível adicionar o profissional à escala.',
				),
			);
		else {
			setFeedback(
				editingLine
					? 'Alterações do profissional salvas.'
					: 'Profissional adicionado à escala multiprofissional.',
			);
			closeProfessionalModal();
			await loadEnvironment({
				nextEstablishment: appliedContext.estabelecimento,
				nextSector: appliedContext.setor,
				mesReferencia: appliedContext.mesReferencia,
				minimumLoadingTime: 1000,
			});
		}
		setSubmitting(false);
	};
	const updateJornadaWeekDay = (setter, day) =>
		setter((current) => ({
			...current,
			diasSemana: current.diasSemana.includes(day)
				? current.diasSemana.filter((item) => item !== day)
				: [...current.diasSemana, day].sort(
						(first, second) => first - second,
					),
		}));
	const updateJornadaWeekDayTime = (setter, day, field, value) =>
		setter((current) => ({
			...current,
			diasSemanaHorarios: {
				...current.diasSemanaHorarios,
				[day]: {
					...(current.diasSemanaHorarios?.[day] || {
						horaInicio: current.horaInicio,
						horaFim: current.horaFim,
					}),
					[field]: value,
				},
			},
		}));
	const submitJornada = async () => {
		const form = isJornadaNewSectorOpen
			? jornadaNewSectorForm
			: jornadaForm;
		if (!form || !jornadaLine || !appliedContext) return;
		const formsToValidate = isJornadaNewSectorOpen
			? [form]
			: jornadaSectorLines.map((line) =>
				jornadaSectorForms[String(getLineRegimeVinculoId(line))],
			).filter(Boolean);
		const invalidForm = formsToValidate.find((candidate) => {
			const recurrence = recurrenceOptions.find((item) => item.value === candidate.recorrencia);
			const needsWeekDays = (recurrence?.tipo_recorrencia || 'SEMANAL') === 'SEMANAL';
			return !candidate.setor || !candidate.regime || !candidate.recorrencia || !candidate.vigenciaInicio || (needsWeekDays && !candidate.diasSemana.length);
		});
		if (invalidForm) {
			setJornadaError(
				'Preencha setor, regime, recorrência, data inicial e os dias obrigatórios de todas as jornadas.',
			);
			return;
		}
		setJornadaSubmitting(true);
		setJornadaError('');
		const regimeEscala = {
			...buildRegimePayload({ form, startDate: form.vigenciaInicio }),
			categoria_id: Number(form.categoria),
		};
		let result;
		if (isJornadaNewSectorOpen) {
			const cpf = getCpfDigits(form.cpf);
			const body = {
				contexto: {
					estabelecimento: appliedContext.estabelecimento,
					setor: Number(form.setor),
					mes_referencia: appliedContext.mesReferencia,
				},
				cpf,
				profissional: {
					nome: form.nome,
					numero_registro: form.numeroConselho || undefined,
					uf_conselho: form.ufConselho || undefined,
					telefone: form.telefone.replace(/\D/g, '') || undefined,
				},
					vinculo_profissional: {
						vinculo_tipo_id: Number(form.vinculoTipo) || undefined,
					categoria_id: Number(form.categoria),
					data_admissao: form.admissao || undefined,
					cargo: form.cargo || undefined,
				},
				regime_escala: regimeEscala,
			};
			const impact = await getProfessionalCreationImpact(cpf, form.setor);
			setProfessionalCreationConfirmation({
				kind: 'jornada-new-sector',
				body,
				...impact,
			});
			setJornadaSubmitting(false);
			return;
			} else {
				const updates = jornadaSectorLines.map((line) => {
					const regimeVinculoId = getLineRegimeVinculoId(line);
					const vinculoId = getLineVinculoId(line);
					const sectorForm = jornadaSectorForms[String(regimeVinculoId)];
					return { line, regimeVinculoId, vinculoId, sectorForm };
				});
				if (updates.some((item) => !item.vinculoId || !item.regimeVinculoId || !item.sectorForm)) {
					setJornadaError(
						'Não foi possível identificar todas as jornadas deste profissional. Atualize a escala e tente novamente.',
				);
				setJornadaSubmitting(false);
				return;
			}
				const results = await Promise.all(updates.map(({ vinculoId, regimeVinculoId, sectorForm }) => {
					const sectorRegime = {
						...buildRegimePayload({ form: sectorForm, startDate: sectorForm.vigenciaInicio }),
						categoria_id: Number(sectorForm.categoria),
					};
					return requestAtualizarVinculoProfissionalMultiprofissional({
						token,
						id: vinculoId,
						body: {
							tipo_vinculo_id: Number(sectorForm.vinculoTipo) || undefined,
							categoria: Number(sectorForm.categoria),
							data_admissao: sectorForm.admissao || null,
							cargo: sectorForm.cargo || undefined,
							regime_escala: {
								...sectorRegime,
								regime_vinculo_id: Number(regimeVinculoId),
						},
						chave_idempotencia: createIdempotencyKey(
							'editar-jornada-multiprofissional',
						),
					},
					});
				}));
				result = results.find((item) => item?.error) || results[0];
		}
		if (result?.error)
			setJornadaError(
				getErrorMessage(
					result,
					'Não foi possível salvar os turnos e setores do profissional.',
				),
			);
		else {
			setFeedback(
				isJornadaNewSectorOpen
					? 'Profissional alocado no novo setor.'
					: 'Turnos e setores atualizados.',
			);
			closeJornadaModal();
			await loadEnvironment({
				nextEstablishment: appliedContext.estabelecimento,
				nextSector: appliedContext.setor,
				mesReferencia: appliedContext.mesReferencia,
				minimumLoadingTime: 1000,
			});
		}
		setJornadaSubmitting(false);
	};
	const submitVacancy = async (event) => {
		event.preventDefault();
		if (
			!appliedContext ||
			!vacancy.categoria ||
			!vacancy.setor ||
			!vacancy.dataInicio ||
			!vacancy.diasSemana.length
		) {
			setError(
				'Preencha setor, categoria, início da vacância e os dias da jornada.',
			);
			return;
		}
		setSubmitting(true);
		setError('');
		const result = await requestAdicionarVacanciaAmbienteMultiprofissional({
			token,
			body: {
				contexto: {
					estabelecimento: appliedContext.estabelecimento,
					setor: Number(vacancy.setor),
					mes_referencia: appliedContext.mesReferencia,
				},
				vacancia: {
					categoria_id: Number(vacancy.categoria),
					vinculo_tipo_id: Number(vacancy.vinculoTipo) || undefined,
					data_inicio_vacancia: vacancy.dataInicio,
					observacao: vacancy.observacao || undefined,
				},
				regime_escala: {
					...buildRegimePayload({
						form: vacancy,
						startDate: vacancy.dataInicio,
					}),
					categoria_id: Number(vacancy.categoria),
				},
			},
		});
		if (result?.error)
			setError(
				getErrorMessage(
					result,
					'Não foi possível adicionar a vacância à escala.',
				),
			);
		else {
			setFeedback('Vacância adicionada à escala multiprofissional.');
			setIsVacancyModalOpen(false);
			await loadEnvironment({
				nextEstablishment: appliedContext.estabelecimento,
				nextSector: appliedContext.setor,
				mesReferencia: appliedContext.mesReferencia,
				minimumLoadingTime: 1000,
			});
		}
		setSubmitting(false);
	};
	const deleteProfessional = async () => {
		const targetId = getLineRegimeVinculoId(deleteTarget);
		if (!targetId || !appliedContext) {
			setError(
				'Não foi possível identificar a alocação selecionada para exclusão. Atualize a escala e tente novamente.',
			);
			return;
		}
		setSubmitting(true);
		setError('');
		const setorAlvo =
			deleteTarget?.setor?.id ||
			deleteTarget?.setor_id ||
			getOperationalSector(appliedContext.setor);
		const detalhe = await requestDetalharLinhaMultiprofissional({
			token,
			estabelecimento: appliedContext.estabelecimento,
			setor: setorAlvo,
			mesReferencia: appliedContext.mesReferencia,
			regimeVinculoId: targetId,
		});
		if (detalhe?.error) {
			setError(
				getErrorMessage(
					detalhe,
					'Não foi possível carregar os dados para exclusão.',
				),
			);
			setSubmitting(false);
			return;
		}
		let result;
		{
			const today = new Date();
			const dataCorte = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
			const idempotencyKey = createIdempotencyKey(
				'encerrar-alocacao-multiprofissional',
			);
			const previewBody = {
				contexto: {
					estabelecimento: appliedContext.estabelecimento,
					setor: detalhe?.linha?.setor?.id || setorAlvo,
					mes_referencia: appliedContext.mesReferencia,
				},
				acao:
					isAllSectorsValue(appliedContext.setor) &&
						!isVacancyLine(deleteTarget) &&
						!deleteTarget?.forceSectorOnly
						? 'ENCERRAR_ALOCACOES_MODALIDADE'
						: 'ENCERRAR_ALOCACAO_SETOR',
				observacao:
					isAllSectorsValue(appliedContext.setor) &&
						!isVacancyLine(deleteTarget) &&
						!deleteTarget?.forceSectorOnly
						? 'Profissional deixa todos os setores desta modalidade.'
						: 'Encerramento da alocação neste setor.',
				regime_vinculo_id: Number(targetId),
				data_corte: dataCorte,
				chave_idempotencia: idempotencyKey,
			};
			const preview = await requestPreverAcoesAmbienteMultiprofissional({
				token,
				body: previewBody,
			});
			if (
				preview?.error ||
				preview?.bloqueado ||
				preview?.valido === false ||
				preview?.acoes?.aplicar?.disponivel === false ||
				preview?.issues?.some((issue) => issue?.bloqueante === true)
			) {
				result = preview?.error
					? preview
					: {
							error: {
								message: getText(
									preview?.resumo?.mensagem,
									preview?.mensagem,
									preview?.orientacao,
									'A exclusão não pode ser aplicada a esta alocação.',
								),
							},
						};
			} else {
				const applyInput = preview?.acoes?.aplicar?.entrada || {
					...previewBody,
					modo_ambiente: 'linha',
					preview_etag: preview?.preview_etag,
				};
				result = await requestAplicarAcoesAmbienteMultiprofissional({
					token,
					body: applyInput,
					previewEtag: preview?.preview_etag,
					idempotencyKey,
				});
			}
		}
		if (result?.error) {
			const rawMessage = normalizeSearch(getErrorMessage(result, ''));
			setError(
				rawMessage.includes('histor') ||
					rawMessage.includes('posterior') ||
					rawMessage.includes('futur')
					? 'O profissional não possui alocações posteriores ao dia de hoje. Por isso, seu histórico de alocações anteriores não pode ser apagado.'
					: getErrorMessage(
							result,
							'Não foi possível excluir o profissional.',
						),
			);
		} else {
			setFeedback('Profissional removido da escala multiprofissional.');
			setDeleteTarget(null);
			await loadEnvironment({
				nextEstablishment: appliedContext.estabelecimento,
				nextSector: appliedContext.setor,
				mesReferencia: appliedContext.mesReferencia,
				minimumLoadingTime: 1000,
			});
		}
		setSubmitting(false);
	};
	const applySelectedCellAction = async (action) => {
		const regimeVinculoId = getLineRegimeVinculoId(selectedCell?.linha);
		if (!selectedCell?.day || !regimeVinculoId || !appliedContext) {
			setError(
				'Não foi possível identificar a jornada do card selecionado. Atualize a escala e tente novamente.',
			);
			return;
		}
		setSideActionSubmitting(true);
		setError('');
		const idempotencyKey = createIdempotencyKey(
			`acao-${String(action).toLowerCase()}`,
		);
		const previewBody = {
			contexto: {
				estabelecimento: appliedContext.estabelecimento,
				setor: getOperationalSector(appliedContext.setor),
				mes_referencia: appliedContext.mesReferencia,
			},
			acao: action,
			regime_vinculo_id: Number(regimeVinculoId),
			modo_ambiente: 'linha',
			chave_idempotencia: idempotencyKey,
			...(action === 'REMOVER_DIA'
				? {
						datas: [selectedCell.day],
						motivo: deleteReason || undefined,
						escopo_exclusao: deleteScope,
					}
				: {}),
			...(action === 'REGISTRAR_AUSENCIA'
				? {
						tipo_item: absenceType,
						periodo: {
							data_inicio:
								absencePeriod.dataInicio || selectedCell.day,
							data_fim:
								absencePeriod.dataFim ||
								absencePeriod.dataInicio ||
								selectedCell.day,
						},
						observacao: ['ATESTADO', 'FOLGA', 'LICENCA'].includes(
							absenceType,
						)
							? absenceObservation || undefined
							: undefined,
					}
				: {}),
		};
		const preview = await requestPreverAcoesAmbienteMultiprofissional({
			token,
			body: previewBody,
		});
		if (preview?.error || preview?.bloqueado || preview?.valido === false) {
			setError(
				preview?.error
					? getErrorMessage(
							preview,
							'Não foi possível validar a ação selecionada.',
						)
					: getText(
							preview?.resumo?.mensagem,
							preview?.mensagem,
							preview?.orientacao,
							'A ação não pode ser aplicada a este card.',
						),
			);
			setSideActionSubmitting(false);
			return;
		}
		const result = await requestAplicarAcoesAmbienteMultiprofissional({
			token,
			body: preview?.acoes?.aplicar?.entrada || {
				...previewBody,
				preview_etag: preview?.preview_etag,
			},
			previewEtag: preview?.preview_etag,
			idempotencyKey,
		});
		if (result?.error)
			setError(
				getErrorMessage(
					result,
					'Não foi possível aplicar a ação selecionada.',
				),
			);
		else {
			setFeedback(
				action === 'REMOVER_DIA'
					? 'Dia removido da jornada.'
					: 'Ausência aplicada à jornada.',
			);
			setIsEditPanelOpen(false);
			setOpenMiniTabs(getDefaultMiniTabs());
			setSelectedCell(null);
			await loadEnvironment({
				nextEstablishment: appliedContext.estabelecimento,
				nextSector: appliedContext.setor,
				mesReferencia: appliedContext.mesReferencia,
				minimumLoadingTime: 1000,
			});
		}
		setSideActionSubmitting(false);
	};
	const selectedCard = Array.isArray(
		selectedCell?.cell?.cards || selectedCell?.cell?.itens,
	)
		? (selectedCell?.cell?.cards || selectedCell?.cell?.itens)[0]
		: selectedCell?.cell?.cards || selectedCell?.cell?.itens;
	const panelTurnoDisplay = getText(
		selectedCard?.turno?.nome,
		selectedCard?.turno?.nome_exibicao,
		selectedCard?.horario?.nome,
		selectedCard?.turno,
		selectedCard?.nome_turno,
		selectedCard?.hora_inicio && selectedCard?.hora_fim
			? `${String(selectedCard.hora_inicio).slice(0, 5)} às ${String(selectedCard.hora_fim).slice(0, 5)}`
			: '',
		selectedCell?.linha?.regime_escala?.hora_inicio &&
			selectedCell?.linha?.regime_escala?.hora_fim
			? `${String(selectedCell.linha.regime_escala.hora_inicio).slice(0, 5)} às ${String(selectedCell.linha.regime_escala.hora_fim).slice(0, 5)}`
			: '',
		selectedCell?.linha?.turno,
	);
	const panelRegimeDisplay = getText(
		selectedCard?.regime?.nome,
		selectedCard?.regime_nome,
		selectedCard?.regime,
		selectedCell?.linha?.regime?.nome,
		selectedCell?.linha?.regime,
		selectedCell?.linha?.regime_escala?.regime?.nome,
		selectedCell?.linha?.regime_escala?.regime_nome,
		selectedCell?.linha?.regime_escala?.regime,
		selectedCell?.linha?.regimeEscala?.regime,
		selectedCell?.linha?.resumo?.regime,
	);
	const selectedAbsenceTab = Object.entries(openMiniTabs).find(
		([, isOpen]) => isOpen,
	)?.[0];
	const absenceTabs = [
		{
			key: 'ferias',
			label: 'Férias',
			type: 'FERIAS',
			color: 'bg-[#6ACEF8]',
			activeColor: 'bg-[#56BFEF]',
		},
		{
			key: 'atestado',
			label: 'Atestado',
			type: 'ATESTADO',
			color: 'bg-[#FB965C]',
			activeColor: 'bg-[#F28747]',
		},
		{
			key: 'folga',
			label: 'Folga',
			type: 'FOLGA',
			color: 'bg-[#8BBD8B]',
			activeColor: 'bg-[#73AF73]',
		},
		{
			key: 'licenca',
			label: 'Licença',
			type: 'LICENCA',
			color: 'bg-[#A476C7]',
			activeColor: 'bg-[#8E5FB7]',
		},
		{
			key: 'troca',
			label: 'Troca',
			color: 'bg-[#967771]',
			activeColor: 'bg-[#7F615C]',
		},
		{
			key: 'falta',
			label: 'Falta',
			type: 'FALTA',
			color: 'bg-[#545454]',
			activeColor: 'bg-[#4B4B4B]',
		},
	];
	const closeSelectedCellPanel = () => {
		setIsEditPanelOpen(false);
		setOpenMiniTabs(getDefaultMiniTabs());
		setSelectedCell(null);
		setAbsencePeriod({ dataInicio: '', dataFim: '' });
		setAbsenceObservation('');
		setIsDeletePanelOpen(false);
		setDeleteReason('');
	};
	const togglePanelTab = (tab) => {
		if (tab.key === 'troca') return;
		setOpenMiniTabs((current) => {
			const willOpen = !current[tab.key];
			return Object.fromEntries(
				Object.keys(current).map((key) => [
					key,
					key === tab.key ? willOpen : false,
				]),
			);
		});
		if (!openMiniTabs[tab.key]) setAbsenceType(tab.type);
		setAbsenceObservation('');
		setIsDeletePanelOpen(false);
	};
	const selectedCellPanel = (
		<div
			className='relative min-w-0 overflow-hidden bg-white border-l border-gray-200'
			style={{
				width: isEditPanelOpen ? `${EDIT_PANEL_WIDTH}px` : '0px',
				transition: 'width 280ms ease',
				pointerEvents: isEditPanelOpen ? 'auto' : 'none',
			}}
			aria-hidden={!isEditPanelOpen}>
			<div
				className='h-full flex flex-col px-3 py-2 gap-1'
				style={{
					width: `${EDIT_PANEL_WIDTH}px`,
					transform: isEditPanelOpen
						? 'translateX(0)'
						: 'translateX(40px)',
					opacity: isEditPanelOpen ? 1 : 0,
					transition: 'transform 280ms ease, opacity 180ms ease',
				}}>
				{selectedCell && (
					<>
						<div className='flex flex-col'>
							<div className='flex items-center justify-between border-gray-200'>
								<div className='text-[13px] font-medium text-[#13335A]'>
									Editar dia{' '}
									{new Date(`${selectedCell.day}T12:00:00`)
										.toLocaleDateString('pt-BR')
										.split('/')[0] || '--'}
								</div>
								<button
									type='button'
									onClick={closeSelectedCellPanel}
									aria-label='Fechar edição'
									className='rounded-md text-xl leading-none text-gray-300 hover:text-gray-600 transition flex items-center justify-center'>
									&times;
								</button>
							</div>
							<div
								title={getLineName(selectedCell.linha)}
								className='max-w-[150px] bg-white text-[#13335A] font-medium rounded-md text-[13px] whitespace-nowrap overflow-hidden text-ellipsis'>
								{getLineName(selectedCell.linha)}
							</div>
							<div className='text-[10px] text-[#b9bec7]'>
								Lembre-se de selecionar o dia inicial e depois
								editar
							</div>
						</div>
						<div className='flex-1 space-y-3'>
							<div>
								<label className='block text-[11px] font-medium text-[#4F5257] mb-1'>
									Regime
								</label>
								<div className='w-full h-[30px] border border-gray-200 rounded-md px-2 text-[12px] outline-none flex items-center cursor-not-allowed bg-[#F3F4F6] text-[#9CA3AF]'>
									{panelRegimeDisplay}
								</div>
							</div>
							<div>
								<label className='block text-[11px] font-medium text-[#4F5257] mb-1'>
									Turno
								</label>
								<div className='w-full h-[30px] border border-gray-200 rounded-md px-2 text-[12px] outline-none flex items-center cursor-not-allowed bg-[#F3F4F6] text-[#9CA3AF]'>
									{panelTurnoDisplay}
								</div>
							</div>
							<div className='space-y-2 pt-1'>
								{absenceTabs.map((tab) => (
									<div
										key={tab.key}
										className={
											openMiniTabs[tab.key]
												? 'space-y-0'
												: 'space-y-1'
										}>
										<button
											type='button'
											onClick={() => togglePanelTab(tab)}
											disabled={tab.key === 'troca'}
											title={
												tab.key === 'troca'
													? 'Troca indisponível para esta escala'
													: undefined
											}
											className={`h-[26px] px-2 flex items-center justify-between w-full text-white transition-all duration-200 ${openMiniTabs[tab.key] ? 'rounded-t-md rounded-b-none' : 'rounded-md'} ${openMiniTabs[tab.key] ? `${tab.activeColor} shadow-inner ring-1 ring-black/10` : tab.color} disabled:cursor-not-allowed disabled:opacity-60`}>
											<span className='text-white text-[11px] font-semibold'>
												{tab.label}
											</span>
											<span
												className={`w-[26px] h-[14px] rounded-full flex items-center px-[2px] ${openMiniTabs[tab.key] ? 'justify-end bg-[#13335A]' : 'justify-start bg-white/40'}`}>
												<span className='w-[10px] h-[10px] rounded-full bg-white' />
											</span>
										</button>
										<div
											className='grid overflow-hidden'
											style={{
												gridTemplateRows: openMiniTabs[
													tab.key
												]
													? '1fr'
													: '0fr',
												transition:
													'grid-template-rows 320ms cubic-bezier(0.22, 1, 0.36, 1)',
												pointerEvents: openMiniTabs[
													tab.key
												]
													? 'auto'
													: 'none',
											}}>
											<div className='min-h-0 overflow-hidden'>
												<div className='space-y-1.5 rounded-b-md border border-gray-200 bg-white px-2 py-2 text-[10px] text-[#666]'>
													{tab.key === 'falta' ? (
														<p>
															Clique em Enviar
															para registrar a
															falta.
														</p>
													) : (
														<>
															<label className='block'>
																Data início
																<input
																	type='date'
																	value={
																		absencePeriod.dataInicio
																	}
																	onChange={(
																		event,
																	) =>
																		setAbsencePeriod(
																			(
																				current,
																			) => ({
																				...current,
																				dataInicio:
																					event
																						.target
																						.value,
																			}),
																		)
																	}
																	className='mt-1 h-[25px] w-full rounded border border-gray-200 px-1 text-[9px] outline-none'
																/>
															</label>
															<label className='block'>
																Data fim
																<input
																	type='date'
																	value={
																		absencePeriod.dataFim
																	}
																	min={
																		absencePeriod.dataInicio ||
																		undefined
																	}
																	onChange={(
																		event,
																	) =>
																		setAbsencePeriod(
																			(
																				current,
																			) => ({
																				...current,
																				dataFim:
																					event
																						.target
																						.value,
																			}),
																		)
																	}
																	className='mt-1 h-[25px] w-full rounded border border-gray-200 px-1 text-[9px] outline-none'
																/>
															</label>
															{[
																'atestado',
																'folga',
																'licenca',
															].includes(
																tab.key,
															) && (
																<label className='block'>
																	Observação
																	<textarea
																		value={
																			absenceObservation
																		}
																		onChange={(
																			event,
																		) =>
																			setAbsenceObservation(
																				event
																					.target
																					.value,
																			)
																		}
																		rows='2'
																		className='mt-1 w-full resize-none rounded border border-gray-200 px-1 py-1 text-[9px] outline-none'
																	/>
																</label>
															)}
														</>
													)}
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
						<div className='space-y-2'>
							<button
								type='button'
								onClick={() => {
									setIsDeletePanelOpen((open) => !open);
									setOpenMiniTabs(getDefaultMiniTabs());
								}}
								disabled={sideActionSubmitting}
								className={`w-full h-[26px] rounded-md bg-[#BD504E] text-[10px] font-semibold text-white hover:bg-[#A94442] disabled:opacity-60 ${isDeletePanelOpen ? 'rounded-b-none' : ''}`}>
								EXCLUIR
							</button>
							{isDeletePanelOpen && (
								<div className='space-y-1 rounded-b-md border border-gray-200 bg-white px-2 py-2 text-[9px] text-[#4F5257]'>
									<label className='block'>
										Como deseja excluir o profissional?
										<select
											value={deleteScope}
											onChange={(event) =>
												setDeleteScope(
													event.target.value,
												)
											}
											className='mt-1 h-[24px] w-full rounded border border-gray-200 bg-white px-1 text-[9px] outline-none'>
											<option value='APENAS_DIA'>
												Apenas deste dia
											</option>
										</select>
									</label>
									<label className='block'>
										Motivo da exclusão
										<textarea
											value={deleteReason}
											onChange={(event) =>
												setDeleteReason(
													event.target.value,
												)
											}
											rows='3'
											className='mt-1 w-full resize-none rounded border border-gray-200 px-1 py-1 text-[9px] outline-none'
										/>
									</label>
								</div>
							)}
							<button
								type='button'
								onClick={() =>
									applySelectedCellAction(
										isDeletePanelOpen
											? 'REMOVER_DIA'
											: 'REGISTRAR_AUSENCIA',
									)
								}
								disabled={
									(isDeletePanelOpen
										? false
										: !selectedAbsenceTab ||
											selectedAbsenceTab === 'troca') ||
									sideActionSubmitting
								}
								className='w-full max-w-[170px] h-[30px] border border-gray-200 rounded-md shadow-sm text-[12px] text-[#7b7d80] hover:bg-gray-50 transition disabled:opacity-50'>
								{sideActionSubmitting
									? 'Enviando...'
									: 'Enviar'}
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	);

	if (user && !canManage) {
		return (
			<div className='flex flex-col min-h-screen font-inter'>
				<NavBar />
				<main className='flex-grow flex items-center justify-center px-4 mt-[80px]'>
					<section className='bg-[#13335A] rounded-md w-full max-w-[720px]'>
						<div className='py-8 px-4 mx-auto max-w-screen-xl lg:py-16 lg:px-6 text-center'>
							<h1 className='mb-4 text-white text-6xl tracking-tight font-extrabold lg:text-7xl'>
								401
							</h1>
							<p className='mb-10 text-3xl tracking-tight font-bold text-slate-100 md:text-4xl'>
								Acesso Negado
							</p>
							<p className='mb-7 text-lg font-light text-gray-300'>
								A tela de gerenciamento de profissionais exige
								permissão de edição da escala multiprofissional.
							</p>
						</div>
					</section>
				</main>
			</div>
		);
	}

	return (
		<div className='flex flex-col min-h-screen font-inter'>
			<NavBar />
			<div className='mt-[80px] mb-4 font-inter'>
				<h1 className='text-center text-[26px] sm:text-[28px] font-semibold text-[#13335A] bg-[#F0F4F8] p-[30px]'>
					Gerenciamento de Profissionais
				</h1>
			</div>
			<main className='flex-grow w-full'>
				<div className='max-w-[1600px] w-[95%] mx-auto flex flex-col gap-4 pb-10 font-inter max-sm:w-full max-sm:px-3'>
					<div className='w-[90%] sm:w-[95%] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 max-w-[1600px] py-2'>
						<Link
							to='/escala-multiprofissional'
							className='inline-flex items-center gap-1.5 text-[18px] font-medium text-[#17395f] hover:text-[#60758f] duration-300 transition-colors'>
							<MdArrowBackIosNew size={15} />
							Voltar
						</Link>
					</div>
					<section className='bg-white border border-gray-200 shadow-md rounded-md'>
						<div className='px-5 py-3'>
							<p className='text-[18px] font-semibold text-[#004A80]'>
								Selecione o setor
							</p>
						</div>
						<hr className='mx-[20px]' />
						<div className='px-5 py-4 flex flex-col gap-3 w-full max-sm:px-3'>
							<div className='flex gap-3 w-full max-sm:flex-col'>
								<div className='flex gap-3 items-end w-full max-sm:flex-col max-sm:items-stretch'>
									{canSelectUnit && (
										<div className='flex flex-col gap-1 relative w-full'>
											<label className='text-[14px] text-[#13335A] font-medium'>
												Unidade
											</label>
											<input
												type='text'
												value={unitSearchTerm}
												onChange={(event) =>
													handleUnitInputChange(
														event.target.value,
													)
												}
												onFocus={() => {
													setShowSectorSuggestions(
														false,
													);
													setShowUnitSuggestions(
														true,
													);
												}}
												onBlur={() =>
													setTimeout(
														() =>
															setShowUnitSuggestions(
																false,
															),
														120,
													)
												}
												placeholder='Selecione a unidade'
												className='border rounded-md text-[13px] font-medium px-2 py-[9.2px] text-[#13335A] bg-white outline-none w-full h-full border-[#E3E3E3]'
											/>
											{showUnitSuggestions && (
												<div className='absolute left-0 right-0 top-full mt-1 z-30 w-full max-h-[220px] overflow-y-auto rounded-[8px] border border-[#E3E3E3] bg-white shadow-sm'>
													{filteredUnits.length ? (
														filteredUnits.map(
															(unit) => (
																<button
																	type='button'
																	key={
																		unit.value
																	}
																	onMouseDown={(
																		event,
																	) => {
																		event.preventDefault();
																		handleSelectUnit(
																			unit,
																		);
																	}}
																	className='block w-full px-3 py-2 text-left text-[14px] text-[#4F5257] hover:bg-[#F3F4F6]'>
																	{unit.label}
																</button>
															),
														)
													) : (
														<div className='px-3 py-2 text-[13px] text-[#9CA3AF]'>
															Nenhuma unidade
															encontrada
														</div>
													)}
												</div>
											)}
										</div>
									)}
									<div className='flex flex-col gap-1 relative w-full'>
										<label className='text-[14px] text-[#13335A] font-medium'>
											Setor
										</label>
										<input
											type='text'
											value={sectorSearchTerm}
											onChange={(event) =>
												handleSectorInputChange(
													event.target.value,
												)
											}
											onFocus={() => {
												if (!establishment) return;
												setShowUnitSuggestions(false);
												setShowSectorSuggestions(true);
											}}
											onBlur={() =>
												setTimeout(
													() =>
														setShowSectorSuggestions(
															false,
														),
													120,
												)
											}
											disabled={!establishment}
											placeholder='Selecione o Setor'
											className='border rounded-md text-[13px] font-medium px-2 py-[9.2px] text-[#13335A] bg-white outline-none w-full h-full disabled:bg-gray-100 disabled:text-gray-400 border-[#E3E3E3]'
										/>
										{showSectorSuggestions && (
											<div className='absolute left-0 right-0 top-full mt-1 z-30 w-full max-h-[220px] overflow-y-auto rounded-[8px] border border-[#E3E3E3] bg-white shadow-sm'>
												{sectorsWithAll.filter(
													(sector) =>
														!normalizeSearch(
															sectorSearchTerm,
														) ||
														normalizeSearch(
															sector.label,
														).includes(
															normalizeSearch(
																sectorSearchTerm,
															),
														) ||
														normalizeSearch(
															sector.identificador,
														).includes(
															normalizeSearch(
																sectorSearchTerm,
															),
														),
												).length ? (
													sectorsWithAll
														.filter(
															(sector) =>
																!normalizeSearch(
																	sectorSearchTerm,
																) ||
																normalizeSearch(
																	sector.label,
																).includes(
																	normalizeSearch(
																		sectorSearchTerm,
																	),
																) ||
																normalizeSearch(
																	sector.identificador,
																).includes(
																	normalizeSearch(
																		sectorSearchTerm,
																	),
																),
														)
														.map((sector) => (
															<button
																type='button'
																key={
																	sector.value
																}
																onMouseDown={(
																	event,
																) => {
																	event.preventDefault();
																	handleSelectSector(
																		sector,
																	);
																}}
																className='block w-full px-3 py-2 text-left text-[14px] text-[#4F5257] hover:bg-[#F3F4F6]'>
																{sector.label}
															</button>
														))
												) : (
													<div className='px-3 py-2 text-[13px] text-[#9CA3AF]'>
														Nenhum setor encontrado
													</div>
												)}
											</div>
										)}
									</div>
								</div>
								<div className='flex items-end justify-end max-sm:w-full'>
									<button
										type='button'
										onClick={handleSelectContext}
										className='bg-white border border-gray-200 hover:bg-gray-50 transition text-[#13335A] shadow-sm text-[14px] font-semibold w-[230px] h-[37px] rounded-md whitespace-nowrap max-sm:w-full'>
										Selecionar
									</button>
								</div>
							</div>
						</div>
					</section>
					{error && !isModalOpen && !isVacancyModalOpen && (
						<div className='rounded-lg border border-[#F5C2C7] bg-[#FDECEE] px-4 py-3 text-[14px] text-[#A61B29]'>
							{error}
						</div>
					)}
					{/* {feedback && (
						<div className='rounded-lg border border-[#B7DEC4] bg-[#ECF9F0] px-4 py-3 text-[14px] text-[#176B37]'>
							{feedback}
						</div>
					)} */}
					{appliedContext && (
						<div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
							<button
								type='button'
								onClick={openProfessionalModal}
								className='flex items-center justify-between gap-2 bg-[#004A80] text-white px-5 py-2 hover:bg-[#0d466c] transition rounded-[7px] shadow-md text-[13px] max-sm:w-full max-sm:justify-center'>
								<span className='text-xl leading-none mb-1'>
									+
								</span>
								<span className='inline-block w-[2px] h-6 bg-[#003D69]' />
								<span>Adicionar novo profissional</span>
							</button>
							<button
								type='button'
								onClick={openVacancyModal}
								className='flex items-center gap-2 bg-white border border-[#BD504E] hover:bg-[#BD504E] hover:text-white text-[#BD504E] text-[13px] font-semibold px-4 py-2 rounded-md transition-all duration-300 shadow-sm max-sm:w-full max-sm:justify-center'>
								Adicionar vacância
							</button>
						</div>
					)}
					{!appliedContext && !loadingEnvironment ? (
						<div className='w-full text-center py-14 text-[#757575] text-[18px] font-medium'>
							Selecione a unidade e o setor para visualizar e
							gerenciar a escala.
						</div>
					) : (
						<section className='bg-white border border-gray-200 shadow-sm rounded-md overflow-x-auto font-inter'>
							{loadingEnvironment ? (
								<div className='p-4'>
									{Array.from({ length: 8 }, (_, index) => (
										<div
											key={index}
											className='flex h-[42px] animate-pulse items-center gap-3 border-b border-gray-100 px-2'>
											<span className='h-7 w-7 rounded-full bg-[#E7EEF5]' />
											<span className='h-3 w-36 rounded bg-[#E7EEF5]' />
											<span className='ml-auto h-3 w-2/5 rounded bg-[#F0F4F8]' />
										</div>
									))}
								</div>
							) : (
								<div className='flex min-w-[1250px] items-stretch'>
									<div className='min-w-[1250px] flex-1'>
										<div className='flex items-stretch border-b border-gray-200'>
											<div className='w-[240px] shrink-0 border-r border-gray-200 px-3 py-1 flex items-center'>
												<span className='text-[13px] font-semibold text-[#13335A]'>
													Profissionais
												</span>
											</div>
											<div className='flex-1 px-3 py-1 flex items-center'>
												<div className='flex-1 text-[13px] font-semibold text-[#13335A] capitalize'>
													{monthLabel}
												</div>
												<div className='flex-1 text-center text-[13px] font-semibold text-[#13335A]'>
													{sectorLabel}
												</div>
												<div className='flex-1 flex items-center justify-end gap-2'>
													<button
														type='button'
														onClick={
															goToCurrentMonth
														}
														disabled={
															loadingEnvironment
														}
														className='flex items-center gap-1 text-[11px] font-normal text-[#4F5257] border border-gray-200 rounded-md px-2 py-1 hover:bg-gray-50 transition disabled:opacity-50'>
														<MdCalendarToday className='w-3.5 h-3.5 text-[#4F5257]' />
														Hoje
													</button>
													<button
														type='button'
														onClick={() =>
															changeCalendarMonth(
																-1,
															)
														}
														disabled={
															loadingEnvironment
														}
														aria-label='Mês anterior'
														className='rounded-full border border-gray-200 hover:bg-gray-50 transition text-[#374151] disabled:opacity-50'>
														<MdChevronLeft className='w-5 h-5 text-[#13335A]' />
													</button>
													<button
														type='button'
														onClick={() =>
															changeCalendarMonth(
																1,
															)
														}
														disabled={
															loadingEnvironment
														}
														aria-label='Próximo mês'
														className='rounded-full border border-gray-200 hover:bg-gray-50 transition text-[#374151] disabled:opacity-50'>
														<MdChevronRight className='w-5 h-5 text-[#13335A]' />
													</button>
												</div>
											</div>
										</div>
										<div className='flex items-stretch border-b border-gray-200 min-h-[46px]'>
											<div className='w-[240px] shrink-0 border-r border-gray-200 px-2 py-1 flex items-center'>
												<div className='h-[26px] border border-gray-200 rounded flex items-center px-1 gap-2 w-full'>
													<input
														type='text'
														value={search}
														onChange={(event) =>
															setSearch(
																event.target
																	.value,
															)
														}
														aria-label='Buscar profissional'
														className='flex-1 h-[15px] bg-white outline-none text-[11px] text-[#13335A]'
													/>
													<button
														type='button'
														aria-label='Buscar profissional'
														className='text-gray-300 hover:text-gray-600 transition scale-x-[-1]'>
														<IoIosSearch className='w-4 h-4' />
													</button>
												</div>
											</div>
											<div
												className='flex-1 grid'
												style={calendarGridStyle}>
												{calendarDays.map(
													(day, index) => {
														const date = toIsoDate(
															day?.data,
														);
														const parsed = new Date(
															`${date}T12:00:00`,
														);
														const isToday =
															date ===
															todayIsoDate;
														return (
															<div
																key={
																	date ||
																	index
																}
																className='relative flex flex-col items-center justify-center'>
																{index <
																	calendarDays.length -
																		1 && (
																	<span
																		aria-hidden='true'
																		className='absolute right-0 top-1/2 -translate-y-1/2 h-[55%] w-px bg-gray-200'
																	/>
																)}
																<span
																	className={`text-[13px] leading-tight ${isToday ? 'font-bold text-[#3A3B3B]' : 'font-normal text-[#4F5257]'}`}>
																	{String(
																		parsed.getDate(),
																	).padStart(
																		2,
																		'0',
																	)}
																</span>
																<span
																	className={`text-[9px] leading-tight ${isToday ? 'font-bold text-[#3A3B3B]' : 'font-medium text-[#6B7280]'}`}>
																	{parsed
																		.toLocaleDateString(
																			'pt-BR',
																			{
																				weekday:
																					'short',
																			},
																		)
																		.slice(
																			0,
																			3,
																		)}
																</span>
															</div>
														);
													},
												)}
											</div>
											<div
												aria-hidden='true'
												className='w-[14px] shrink-0 border-l border-gray-100 bg-white'
											/>
										</div>
										<div
											className='overflow-y-scroll [scrollbar-gutter:stable] [&::-webkit-scrollbar]:w-[14px] [&::-webkit-scrollbar-track]:bg-[#F8FAFC] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#CBD5E1]'
											style={{
												height: `${TABLE_BODY_HEIGHT}px`,
											}}>
											<div className='min-h-full'>
												{rowsToRender.map(
													(linha, rowIndex) => {
														const isPlaceholder =
															Boolean(
																linha.placeholder,
															);
														return (
															<div
																key={
																	linha?.linha_id ||
																	rowIndex
																}
																className='flex items-stretch border-b border-gray-100 last:border-b-0'
																style={{
																	height: `${TABLE_ROW_HEIGHT}px`,
																}}>
																<div className='w-[240px] shrink-0 border-r border-gray-200 px-2 flex items-center'>
																	{!isPlaceholder && (
																		<div className='w-full flex items-center gap-2'>
																			<span
																				className={`h-[30px] w-[30px] shrink-0 rounded-full text-white text-[14.3px] font-semibold flex items-center justify-center ${isVacancyLine(linha) ? 'bg-[#BD504E]' : 'bg-[#4D6C92]'}`}>
																				D
																			</span>
																			<div className='min-w-0 flex-1'>
																				<div className='truncate text-[12px] font-semibold text-[#13335A]'>
																					{getLineName(
																						linha,
																					)}
																				</div>
																				<div className='truncate text-[10px] font-medium text-[#6B7280]'>
																					{getText(
																						linha.categoria,
																						linha
																							.resumo
																							?.categoria,
																						linha.vinculo,
																					)}
																				</div>
																			</div>
																<div className='grid h-9 w-9 shrink-0 grid-cols-2 grid-rows-2 gap-px'>
																	{!isVacancyLine(linha) && isHistoricalReadOnlyLine(linha, appliedContext?.mesReferencia) ? (
																		<button type='button' aria-label='Visualizar profissional' title='Histórico da competência — somente leitura' onClick={() => openEditProfessionalModal(linha, 'visualizacao')} className='col-start-2 row-start-2 flex h-4 w-4 items-center justify-center rounded-[3px] border border-gray-200 hover:bg-gray-50'>
																			<MdVisibility className='h-3.5 w-3.5 text-[#004A80]' />
																		</button>
																	) : !isVacancyLine(linha) && (
																		<>
																						{isAllSectorsValue(
																							appliedContext?.setor,
																						) && (
																							<button
																								type='button'
																								aria-label='Editar turnos e setores'
																								onClick={() =>
																									openEditProfessionalModal(
																										linha,
																										'jornada',
																									)
																								}
																								className='col-start-1 row-start-2 flex h-4 w-4 items-center justify-center rounded-[3px] border border-gray-200 hover:bg-gray-50'>
																								<MdSettings className='h-3.5 w-3.5 text-[#004A80]' />
																							</button>
																						)}
																						<button
																							type='button'
																							aria-label='Editar profissional'
																							onClick={() =>
																				openEditProfessionalModal(
																					linha,
																					isAllSectorsValue(appliedContext?.setor) ? 'cadastro' : 'cadastro-escala',
																				)
																							}
																							className='col-start-2 row-start-2 flex h-4 w-4 items-center justify-center rounded-[3px] border border-gray-200 hover:bg-gray-50'>
																							<MdOutlineEdit className='h-3.5 w-3.5 text-[#004A80]' />
																						</button>
																					</>
																				)}
																	{!isHistoricalReadOnlyLine(linha, appliedContext?.mesReferencia) && <button
																					type='button'
																					aria-label={
																						isVacancyLine(
																							linha,
																						)
																							? 'Excluir vacância'
																							: 'Excluir profissional'
																					}
																					onClick={() =>
																						setDeleteTarget(
																							linha,
																						)
																					}
																					className='col-start-2 row-start-1 flex h-4 w-4 items-center justify-center rounded-[3px] border border-gray-200 hover:bg-gray-50'>
																					<MdDeleteOutline className='h-4 w-4 text-[#BD504E]' />
																	</button>}
																			</div>
																		</div>
																	)}
																</div>
																<div
																	className='flex-1 grid'
																	style={
																		calendarGridStyle
																	}>
																	{calendarDays.map(
																		(
																			day,
																			index,
																		) => {
																			const iso =
																				toIsoDate(
																					day?.data,
																				);
																			const cell =
																				isPlaceholder
																					? null
																					: getCellForDay(
																							linha,
																							iso,
																						);
																			const cards =
																				cell?.cards ||
																				cell?.itens ||
																				[];
																			const card =
																				Array.isArray(
																					cards,
																				)
																					? cards[0]
																					: cards;
																			const status =
																				String(
																					card?.tipo_item ||
																						cell?.tipo_item ||
																						cell?.status ||
																						'',
																				).toUpperCase();
																			const active =
																				isAllocatedCell(
																					{
																						cell,
																						card,
																						status,
																					},
																				);
																			const color =
																				status.includes(
																					'VAC',
																				)
																					? 'bg-[#BD504E]'
																					: status.includes(
																								'FER',
																						  )
																						? 'bg-[#6ACEF8]'
																						: status.includes(
																									'ATE',
																							  )
																							? 'bg-[#FB965C]'
																							: active
																								? 'bg-[#4D6C92]'
																								: 'bg-white';
																			return (
																				<div
																					key={`${iso || index}-${linha?.linha_id || rowIndex}`}
																					className='h-full w-full flex items-center justify-center'>
																					{active &&
																					!isPlaceholder ? (
																						<button
																							type='button'
																							onClick={() => {
																								setSelectedCell(
																									{
																										linha,
																										day: iso,
																										cell,
																									},
																								);
																								setAbsencePeriod(
																									{
																										dataInicio:
																											iso,
																										dataFim:
																											iso,
																									},
																								);
																								setAbsenceObservation(
																									'',
																								);
																								setIsDeletePanelOpen(
																									false,
																								);
																								setDeleteReason(
																									'',
																								);
																								setOpenMiniTabs(
																									getDefaultMiniTabs(),
																								);
																								setIsEditPanelOpen(
																									true,
																								);
																							}}
																							className={`h-full w-full max-h-[30px] max-w-[30px] rounded-[4px] mx-auto border ${color} border-transparent text-white text-[15px] font-normal flex items-center justify-center hover:brightness-95`}>
																							{getCardDisplayLabel(
																								{
																									card,
																									cell,
																									status,
																								},
																							)}
																						</button>
																					) : (
																						<span className='h-full w-full max-h-[30px] max-w-[30px] rounded-[4px] border border-[#CBD5E1] bg-white' />
																					)}
																				</div>
																			);
																		},
																	)}
																</div>
															</div>
														);
													},
												)}
											</div>
										</div>
										<div className='flex flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4 py-3 border-t border-gray-200 bg-white'>
											{FALLBACK_LEGEND.map(
												({ color, label }) => (
													<div
														key={label}
														className='flex items-center gap-1.5'>
														<span
															className={`w-3 h-3 rounded-[2px] inline-block ${color}`}
														/>
														<span className='text-[10px] text-[#6B7280]'>
															{label}
														</span>
													</div>
												),
											)}
											{FALLBACK_LEGEND_TEXT.map(
												({ abbreviation, label }) => (
													<div
														key={label}
														className='flex items-center gap-1'>
														<span className='text-[10px] font-normal px-1 h-[15px] text-[#4F5257] border rounded-sm flex items-center justify-center'>
															{abbreviation}
														</span>
														<span className='text-[10px] text-[#6B7280]'>
															{label}
														</span>
													</div>
												),
											)}
										</div>
									</div>
									{selectedCellPanel}
								</div>
							)}
						</section>
					)}
				</div>
			</main>
			{false && isModalOpen && (
				<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3'>
					<form
						onSubmit={submitProfessional}
						className='relative max-h-[95vh] w-full max-w-[720px] overflow-y-auto rounded-[3px] border border-[#D8DEE8] bg-white p-4 shadow-2xl'>
						<button
							type='button'
							onClick={closeProfessionalModal}
							aria-label='Fechar'
							className='absolute right-2 top-0 text-[22px] leading-none text-[#9AA6B2]'>
							&times;
						</button>
						{editingLine && (
							<p className='absolute left-4 top-3 text-[11px] font-semibold text-[#004A80]'>
								{editingScope === 'jornada'
									? 'Editar turnos e setores'
									: 'Editar profissional'}
							</p>
						)}
						<div className='flex items-center justify-center gap-2 pt-4'>
							<label className='text-[11px] font-medium text-[#13335A]'>
								CPF
							</label>
							<input
								value={professional.cpf}
								onChange={(event) =>
									setProfessional((current) => ({
										...current,
										cpf: event.target.value,
									}))
								}
								maxLength='14'
								placeholder='123456789-00'
								className='h-[22px] w-[140px] rounded-[3px] border border-[#DDE3EA] px-2 text-[10px] outline-none'
							/>
							<button
								type='button'
								onClick={searchProfessionalByCpf}
								disabled={submitting}
								className='h-[22px] rounded-[3px] border border-gray-200 px-4 text-[10px] text-[#596675] shadow-sm hover:bg-gray-50'>
								Pesquisar
							</button>
						</div>
						<div className='mt-5 space-y-2 text-[#13335A]'>
							{isAllSectorsValue(appliedContext?.setor) && (
								<label className='flex flex-col gap-1 rounded-[3px] border border-[#C9D7E7] bg-[#F4F8FC] p-2 text-[10px] font-medium'>
									Setor
									<SearchableSetorSelect
										value={professional.setor}
										options={sectors}
										onChange={(value) =>
											setProfessional((current) => ({
												...current,
												setor: value,
											}))
										}
										placeholder='Selecione o setor'
										emptyMessage='Nenhum setor encontrado'
										menuPortal
										className='h-[24px] rounded-[3px] border border-[#DDE3EA] bg-white px-2 text-[10px] font-normal outline-none'
									/>
								</label>
							)}
							<label className='flex flex-col gap-1 text-[10px] font-medium'>
								Nome
								<input
									value={professional.nome}
									onChange={(event) =>
										setProfessional((current) => ({
											...current,
											nome: event.target.value,
										}))
									}
									placeholder='Nome completo'
									className='h-[23px] rounded-[3px] border border-[#DDE3EA] px-2 text-[10px] font-normal outline-none'
								/>
							</label>
							<div className='grid grid-cols-2 gap-2'>
								<label className='flex flex-col gap-1 text-[10px] font-medium'>
									Admissão
									<input
										type='date'
										value={professional.admissao}
										onChange={(event) =>
											setProfessional((current) => ({
												...current,
												admissao: event.target.value,
											}))
										}
										className='h-[23px] rounded-[3px] border border-[#DDE3EA] px-2 text-[10px] font-normal outline-none'
									/>
								</label>
								<label className='flex flex-col gap-1 text-[10px] font-medium'>
									Vínculo
									<select
										value={professional.vinculoTipo}
										onChange={(event) =>
											setProfessional((current) => ({
												...current,
												vinculoTipo: event.target.value,
											}))
										}
										className='h-[23px] rounded-[3px] border border-[#DDE3EA] bg-white px-2 text-[10px] font-normal outline-none'>
										<option value=''>
											Selecione o vínculo
										</option>
										{vinculoTypes.map((item) => (
											<option
												key={item.value}
												value={item.value}>
												{item.label}
											</option>
										))}
									</select>
								</label>
							</div>
							<div className='grid grid-cols-6 gap-2'>
								<label className='col-span-3 flex flex-col gap-1 text-[10px] font-medium'>
									Cargo
									<input
										value={professional.cargo}
										onChange={(event) =>
											setProfessional((current) => ({
												...current,
												cargo: event.target.value,
											}))
										}
										placeholder='Profissional'
										className='h-[23px] rounded-[3px] border border-[#DDE3EA] px-2 text-[10px] font-normal outline-none'
									/>
								</label>
								<label className='col-span-2 flex flex-col gap-1 text-[10px] font-medium'>
									Número do conselho
									<input
										value={professional.numeroConselho}
										onChange={(event) =>
											setProfessional((current) => ({
												...current,
												numeroConselho:
													event.target.value,
											}))
										}
										placeholder='0000000'
										className='h-[23px] rounded-[3px] border border-[#DDE3EA] px-2 text-[10px] font-normal outline-none'
									/>
								</label>
								<label className='col-span-1 flex flex-col gap-1 text-[10px] font-medium'>
									UF
									<select
										value={professional.ufConselho}
										onChange={(event) =>
											setProfessional((current) => ({
												...current,
												ufConselho: event.target.value,
											}))
										}
										className='h-[23px] rounded-[3px] border border-[#DDE3EA] bg-white px-1 text-[10px] font-normal outline-none'>
										<option value='RJ'>RJ</option>
										<option value='SP'>SP</option>
										<option value='MG'>MG</option>
									</select>
								</label>
							</div>
							<div className='grid grid-cols-2 gap-2'>
								<label className='flex flex-col gap-1 text-[10px] font-medium'>
									Categoria
									<select
										value={professional.categoria}
										onChange={(event) =>
											setProfessional((current) => ({
												...current,
												categoria: event.target.value,
											}))
										}
										className='h-[23px] rounded-[3px] border border-[#DDE3EA] bg-white px-2 text-[10px] font-normal outline-none'>
										<option value=''>
											Área de atuação
										</option>
										{categories.map((item) => (
											<option
												key={item.value}
												value={item.value}>
												{item.label}
											</option>
										))}
									</select>
								</label>
								<label className='flex flex-col gap-1 text-[10px] font-medium'>
									Telefone
									<input
										value={professional.telefone}
										onChange={(event) =>
											setProfessional((current) => ({
												...current,
												telefone: formatPhone(
													event.target.value,
												),
											}))
										}
										maxLength='15'
										inputMode='numeric'
										placeholder='(00) 0 0000-0000'
										className='h-[23px] rounded-[3px] border border-[#DDE3EA] px-2 text-[10px] font-normal outline-none'
									/>
								</label>
							</div>
							<div className='grid grid-cols-3 gap-2'>
								<label className='flex flex-col gap-1 text-[10px] font-medium'>
									Regime
									<select
										value={professional.regime}
										onChange={(event) =>
											setProfessional((current) => ({
												...current,
												regime: event.target.value,
											}))
										}
										className='h-[23px] rounded-[3px] border border-[#DDE3EA] bg-white px-2 text-[10px] font-normal outline-none'>
										{availableRegimes.map((item) => (
											<option
												key={item.value}
												value={item.value}>
												{item.label}
											</option>
										))}
									</select>
								</label>
								<label className='flex flex-col gap-1 text-[10px] font-medium'>
									Recorrência
									<select
										value={professional.recorrencia}
										onChange={(event) =>
											setProfessional((current) => ({
												...current,
												recorrencia: event.target.value,
											}))
										}
										className='h-[23px] rounded-[3px] border border-[#DDE3EA] bg-white px-2 text-[10px] font-normal outline-none'>
										{recurrenceOptions.map((item) => (
											<option
												key={item.value}
												value={item.value}>
												{item.label}
											</option>
										))}
									</select>
								</label>
								<label className='flex flex-col gap-1 text-[10px] font-medium'>
									Data inicial
									<input
										type='date'
										value={professional.vigenciaInicio}
										onChange={(event) =>
											setProfessional((current) => ({
												...current,
												vigenciaInicio:
													event.target.value,
											}))
										}
										className='h-[23px] rounded-[3px] border border-[#DDE3EA] px-2 text-[10px] font-normal outline-none'
									/>
								</label>
							</div>
							{professional.regime &&
								professional.recorrencia &&
								professional.vigenciaInicio && (
									<div className='rounded-[3px] border border-[#DDE3EA] bg-[#FAFCFE] p-2'>
										<p className='mb-2 text-[10px] font-medium'>
											Dias e horários da jornada
										</p>
										<div className='grid grid-cols-2 gap-2'>
											<label className='flex flex-col gap-1 text-[10px] font-medium'>
												Hora inicial
												<input
													type='time'
													value={
														professional.horaInicio
													}
													onChange={(event) =>
														setProfessional(
															(current) => ({
																...current,
																horaInicio:
																	event.target
																		.value,
															}),
														)
													}
													className='h-[23px] rounded-[3px] border border-[#DDE3EA] px-2 text-[10px] font-normal outline-none'
												/>
											</label>
											<label className='flex flex-col gap-1 text-[10px] font-medium'>
												Hora final
												<input
													type='time'
													value={professional.horaFim}
													onChange={(event) =>
														setProfessional(
															(current) => ({
																...current,
																horaFim:
																	event.target
																		.value,
															}),
														)
													}
													className='h-[23px] rounded-[3px] border border-[#DDE3EA] px-2 text-[10px] font-normal outline-none'
												/>
											</label>
										</div>
										{selectedRecurrence.tipo_recorrencia ===
											'SEMANAL' && (
											<div className='mt-2 flex flex-wrap gap-1.5'>
												{WEEK_DAYS.map((day) => (
													<button
														type='button'
														key={day.value}
														onClick={() =>
															toggleProfessionalWeekDay(
																day.value,
															)
														}
														className={`rounded-[3px] border px-2 py-1 text-[10px] ${professional.diasSemana.includes(day.value) ? 'border-[#004A80] bg-[#004A80] text-white' : 'border-[#DDE3EA] bg-white text-[#4F5257]'}`}>
														{day.label}
													</button>
												))}
											</div>
										)}
										{selectedRecurrence.tipo_recorrencia ===
											'ROTACAO_PERIODICA' && (
											<div className='mt-2 space-y-2'>
												{professional.semanasRotacao.map(
													(week, weekIndex) => (
														<div key={weekIndex}>
															<p className='mb-1 text-[10px] text-[#667085]'>
																Semana{' '}
																{weekIndex + 1}
															</p>
															<div className='flex flex-wrap gap-1.5'>
																{WEEK_DAYS.map(
																	(day) => (
																		<button
																			type='button'
																			key={
																				day.value
																			}
																			onClick={() =>
																				toggleProfessionalRotationWeekDay(
																					weekIndex,
																					day.value,
																				)
																			}
																			className={`rounded-[3px] border px-2 py-1 text-[10px] ${week.includes(day.value) ? 'border-[#004A80] bg-[#004A80] text-white' : 'border-[#DDE3EA] bg-white text-[#4F5257]'}`}>
																			{
																				day.label
																			}
																		</button>
																	),
																)}
															</div>
														</div>
													),
												)}
											</div>
										)}
										{selectedRecurrence.tipo_recorrencia ===
											'DATAS_ESPECIFICAS' && (
											<div className='mt-2 space-y-1.5'>
												<div className='flex items-center justify-between'>
													<p className='text-[10px] text-[#667085]'>
														Datas em que a jornada
														será criada
													</p>
													<button
														type='button'
														onClick={
															addSpecificDate
														}
														className='rounded border border-[#DDE3EA] bg-white px-2 py-1 text-[10px] text-[#004A80]'>
														+ Data específica
													</button>
												</div>
												{professional.datasEspecificas.map(
													(date, index) => (
														<div
															className='flex items-center gap-2'
															key={`${date}-${index}`}>
															<input
																type='date'
																value={date}
																onChange={(
																	event,
																) =>
																	updateSpecificDate(
																		index,
																		event
																			.target
																			.value,
																	)
																}
																className='h-[23px] flex-1 rounded-[3px] border border-[#DDE3EA] px-2 text-[10px] outline-none'
															/>
															<button
																type='button'
																onClick={() =>
																	removeSpecificDate(
																		index,
																	)
																}
																className='text-[10px] text-[#B42318]'>
																Remover
															</button>
														</div>
													),
												)}
											</div>
										)}
									</div>
								)}
							{error && (
								<p className='text-[11px] text-[#B42318]'>
									{error}
								</p>
							)}
							<div className='flex justify-center pt-4'>
								<button
									type='submit'
									disabled={submitting}
									className='h-[24px] w-[115px] rounded-[3px] border border-gray-200 text-[10px] text-[#697586] shadow-sm hover:bg-gray-50 disabled:opacity-60'>
									{submitting ? 'Enviando...' : 'Enviar'}
								</button>
							</div>
						</div>
					</form>
				</div>
			)}
			{isJornadaModalOpen && (
				<div
					className='fixed inset-x-0 bottom-0 top-20 z-50 flex items-center justify-center overflow-hidden px-4 py-4 max-sm:px-2'
					role='dialog'
					aria-modal='true'
					aria-labelledby='jornada-multi-title'>
					<button
						type='button'
						className='absolute inset-0 bg-black/30'
						onClick={closeJornadaModal}
						aria-label='Fechar modal'
					/>
					<div className='relative flex w-full max-w-[980px] min-h-0 max-h-[calc(100vh-7rem)] flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow-xl'>
						<div className='flex min-h-[38px] shrink-0 items-center justify-between border-b border-gray-100 bg-[#fcfcfc] px-4'>
							<h3
								id='jornada-multi-title'
								className='truncate text-[16px] font-semibold text-[#13335A]'>
								{getLineName(jornadaLine || {})}
							</h3>
							<button
								type='button'
								onClick={closeJornadaModal}
								className='flex h-8 w-8 items-center justify-center rounded-full text-3xl leading-none text-gray-400 transition-all duration-300 hover:text-gray-600'
								aria-label='Fechar'>
								&times;
							</button>
						</div>
						<div className='min-h-0 flex-1 overflow-y-auto px-5 py-4 font-inter max-sm:px-3'>
							<p className='mb-4 text-[14px] font-medium leading-relaxed text-[#8f8f8f]'>
								Confira e ajuste os turnos deste profissional
								neste ou em outros setores
							</p>
							{/* <p className='mb-4 text-[11px] text-[#667085]'>
								{getText(
									jornadaLine?.categoria,
									jornadaLine?.resumo?.categoria,
								)}{' '}
								·{' '}
								{getText(
									jornadaLine?.vinculo_profissional
										?.vinculo_tipo,
									jornadaLine?.vinculo_tipo,
									jornadaLine?.vinculo,
								)}
							</p> */}
								{jornadaError && (
								<div className='mb-3 rounded-md border border-[#F5C2C7] bg-[#FDECEE] px-3 py-2 text-[12px] leading-relaxed text-[#A61B29]'>
									{jornadaError}
								</div>
								)}
								<div className='space-y-3'>
									{jornadaSectorLines.map((line) => {
										const key = String(getLineRegimeVinculoId(line));
										const sectorForm = jornadaSectorForms[key];
										if (!sectorForm) return null;
										const updateForm = (updater) => setJornadaSectorForms((current) => ({ ...current, [key]: typeof updater === 'function' ? updater(current[key]) : updater }));
										return <JornadaSetorForm key={key} form={sectorForm} setorName={getText(line?.setor?.nome, line?.setor_nome, line?.setor?.nome_exibicao, line?.setor, 'Setor')} sectors={sectors} regimes={modalRegimes.length ? modalRegimes : availableRegimes} recurrenceOptions={recurrenceOptions} onChange={(field, value) => updateForm((current) => ({ ...current, [field]: value }))} onToggleDay={(day) => updateJornadaWeekDay(updateForm, day)} onChangeDayTime={(day, field, value) => updateJornadaWeekDayTime(updateForm, day, field, value)} onAddSpecificDate={() => updateForm((current) => ({ ...current, datasEspecificas: [...current.datasEspecificas, current.vigenciaInicio].filter(Boolean) }))} onChangeSpecificDate={(index, value) => updateForm((current) => ({ ...current, datasEspecificas: current.datasEspecificas.map((date, dateIndex) => dateIndex === index ? value : date) }))} onRemoveSpecificDate={(index) => updateForm((current) => ({ ...current, datasEspecificas: current.datasEspecificas.filter((_, dateIndex) => dateIndex !== index) }))} onDeleteSector={() => { closeJornadaModal(); setDeleteTarget({ ...line, forceSectorOnly: true }); }} />;
									})}
								</div>
							{isJornadaNewSectorOpen && (
								<div className='mt-4'>
									<JornadaSetorForm
										form={jornadaNewSectorForm}
										setorName='Novo setor'
										sectors={sectors}
										regimes={
											modalRegimes.length
												? modalRegimes
												: availableRegimes
										}
										recurrenceOptions={recurrenceOptions}
										isNewSector
										onCloseNewSector={() => {
											setIsJornadaNewSectorOpen(false);
											setJornadaNewSectorForm({
												...jornadaForm,
												setor: '',
											});
											setJornadaError('');
										}}
										onChange={(field, value) =>
											setJornadaNewSectorForm(
												(current) => ({
													...current,
													[field]: value,
												}),
											)
										}
										onToggleDay={(day) =>
											updateJornadaWeekDay(
												setJornadaNewSectorForm,
												day,
											)
										}
										onChangeDayTime={(day, field, value) =>
											updateJornadaWeekDayTime(
												setJornadaNewSectorForm,
												day,
												field,
												value,
											)
										}
										onAddSpecificDate={() =>
											setJornadaNewSectorForm(
												(current) => ({
													...current,
													datasEspecificas: [
														...current.datasEspecificas,
														current.vigenciaInicio,
													].filter(Boolean),
												}),
											)
										}
										onChangeSpecificDate={(index, value) =>
											setJornadaNewSectorForm(
												(current) => ({
													...current,
													datasEspecificas:
														current.datasEspecificas.map(
															(
																date,
																dateIndex,
															) =>
																dateIndex ===
																index
																	? value
																	: date,
														),
												}),
											)
										}
										onRemoveSpecificDate={(index) =>
											setJornadaNewSectorForm(
												(current) => ({
													...current,
													datasEspecificas:
														current.datasEspecificas.filter(
															(_, dateIndex) =>
																dateIndex !==
																index,
														),
												}),
											)
										}
									/>
								</div>
							)}
							{!isJornadaNewSectorOpen && (
								<div className='mt-6 flex justify-center'>
									<button
										type='button'
										onClick={() => {
											setJornadaNewSectorForm({
												...jornadaForm,
												setor: '',
											});
											setIsJornadaNewSectorOpen(true);
											setJornadaError('');
										}}
										className='flex h-8 w-8 items-center justify-center rounded-md border border-[#004A80] bg-white text-[22px] leading-none text-[#004A80] shadow-sm transition-all duration-300 hover:bg-[#EEF4FA]'
										aria-label='Adicionar em outro setor'>
										+
									</button>
								</div>
							)}
						</div>
						<div className='mt-auto flex shrink-0 justify-center gap-3 border-t border-gray-100 px-4 py-3'>
							<button
								type='button'
								onClick={closeJornadaModal}
								disabled={jornadaSubmitting}
								className='h-[34px] w-[130px] rounded-md border border-gray-200 text-[13px] text-[#7b7d80] shadow-md transition hover:bg-gray-50 disabled:opacity-60'>
								Fechar
							</button>
							<button
								type='button'
								onClick={submitJornada}
								disabled={jornadaSubmitting || !jornadaForm}
								className='h-[34px] w-[150px] rounded-md border border-[#004A80] bg-[#004A80] text-[13px] text-white shadow-md transition hover:bg-[#0d466c] disabled:cursor-not-allowed disabled:opacity-60'>
								{jornadaSubmitting
									? 'Salvando...'
									: isJornadaNewSectorOpen
										? 'Criar escala'
										: 'Salvar'}
							</button>
						</div>
					</div>
				</div>
			)}
			{professionalCreationConfirmation && (
				<div
					className='fixed inset-0 z-[70] flex items-center justify-center px-4'
					role='dialog'
					aria-modal='true'
					aria-labelledby='professional-creation-confirmation-title'>
					<button
						type='button'
						className='absolute inset-0 bg-black/30'
						disabled={submitting}
						onClick={() => setProfessionalCreationConfirmation(null)}
						aria-label='Cancelar confirmação de criação'
					/>
					<section className='relative w-full max-w-[520px] overflow-hidden rounded-md border border-gray-200 bg-white shadow-xl'>
						<div className='border-b border-gray-100 bg-[#fcfcfc] px-4 py-3'>
							<h3
								id='professional-creation-confirmation-title'
								className='text-[16px] font-semibold text-[#13335A]'>
									{professionalCreationConfirmation.sectors.length
										? 'Confirmar inserção do profissional em outro setor'
										: 'Confirmar criação de jornada'}
							</h3>
						</div>
						<div className='px-4 py-4 text-[13px] leading-relaxed text-[#4F5257]'>
							{professionalCreationConfirmation.sectors.length ? (
								<>
									<p>
										Os horários e dias criados neste novo setor influenciarão e
										serão influenciados pela escala de outros setores criados
										anteriormente, como em{' '}
										<strong>
											{professionalCreationConfirmation.sectors.join(', ')}
										</strong>
										.
									</p>
									<p className='mt-2'>
										Ausências, exclusões ou alterações feitas nas escalas dos setores
										anteriormente criados podem afetar a criação de novos dias neste
										setor.
									</p>
								</>
								) : (
									<>
										<p>
											O profissional será adicionado à escala deste setor conforme o
											período escolhido para ele. Posteriormente, a inserção deste mesmo
											profissional em outros setores será afetada pelos dias e horários
											definidos anteriormente.
										</p>
										<p className='mt-2'>
											Ele ocupará apenas o período selecionado desse horário. Por isso,
											ausências, exclusões e alterações feitas no setor-base também
											podem afetar este novo horário.
										</p>
								</>
							)}
						</div>
						<div className='flex justify-end gap-3 border-t border-gray-100 px-4 py-3'>
							<button
								type='button'
								disabled={submitting}
								onClick={() => setProfessionalCreationConfirmation(null)}
								className='h-[34px] rounded-md border border-gray-200 px-4 text-[13px] text-[#4F5257] transition hover:bg-gray-50 disabled:opacity-60'>
								Cancelar
							</button>
							<button
								type='button'
								disabled={submitting}
								onClick={confirmProfessionalCreation}
								className='h-[34px] rounded-md border border-[#004A80] bg-[#004A80] px-4 text-[13px] text-white transition hover:bg-[#0d466c] disabled:opacity-60'>
								{submitting ? 'Confirmando...' : 'Confirmar'}
							</button>
						</div>
					</section>
				</div>
			)}
			{isModalOpen && (
				<NovoProfissionalMultiprofissionalModal
					professional={professional}
					setProfessional={setProfessional}
					isAllSectors={isAllSectorsValue(appliedContext?.setor)}
					sectors={sectors}
					categories={
						modalCategories.length ? modalCategories : categories
					}
					vinculoTypes={vinculoTypes}
					regimes={
						modalRegimes.length ? modalRegimes : availableRegimes
					}
					recurrenceOptions={recurrenceOptions}
					selectedRecurrence={selectedRecurrence}
					weekDays={WEEK_DAYS}
					isEditing={Boolean(editingLine)}
					editingScope={editingScope}
					submitting={submitting}
					error={modalError}
					fieldErrors={modalFieldErrors}
					onClearFieldError={clearModalFieldError}
					onClose={closeProfessionalModal}
					onSearchCpf={searchProfessionalByCpf}
					onSubmit={submitProfessional}
					formatCpf={formatCpf}
					formatPhone={formatPhone}
					onToggleWeekDay={toggleProfessionalWeekDay}
					onUpdateWeekDayTime={updateProfessionalWeekDayTime}
					onToggleRotationWeekDay={toggleProfessionalRotationWeekDay}
					onAddSpecificDate={addSpecificDate}
					onUpdateSpecificDate={updateSpecificDate}
					onRemoveSpecificDate={removeSpecificDate}
					onSectorSelected={loadCreationOptions}
				/>
			)}
			{isVacancyModalOpen && (
				<div
					className='fixed inset-x-0 bottom-0 top-20 z-50 flex items-center justify-center px-4 py-4'
					role='dialog'
					aria-modal='true'
					aria-label='Adicionar vacância'>
					<button
						type='button'
						className='absolute inset-0 bg-black/30'
						onClick={() => setIsVacancyModalOpen(false)}
						aria-label='Fechar modal'
					/>
					<form
						onSubmit={submitVacancy}
						className='relative flex max-h-[calc(100vh-7rem)] w-full max-w-[780px] flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow-xl'>
						<div className='flex h-[30px] shrink-0 justify-end px-2'>
							<button
								type='button'
								onClick={() => setIsVacancyModalOpen(false)}
								aria-label='Fechar'
								className='text-3xl leading-none text-gray-400'>
								&times;
							</button>
						</div>
						<div className='mx-5 rounded-[4px] bg-[#BD504E] py-2 text-center text-[14px] font-bold text-white'>
							VACÂNCIA
						</div>
						<hr className='mx-5 mt-4 border-gray-200' />
						<div className='grid grid-cols-1 gap-x-3 gap-y-3 overflow-y-auto px-5 py-4 sm:grid-cols-2'>
							<label className='sm:col-span-2 flex flex-col gap-1 text-[13px] font-medium text-[#13335A]'>
								Setor
								<select
									value={vacancy.setor}
									onChange={(event) =>
										setVacancy((current) => ({
											...current,
											setor: event.target.value,
										}))
									}
									className='h-[34px] rounded-md border border-[#E3E3E3] bg-white px-3 text-[13px] font-normal text-gray-600 outline-none'>
									<option value=''>Selecione o setor</option>
									{sectors.map((item) => (
										<option
											key={item.value}
											value={item.value}>
											{item.label}
										</option>
									))}
								</select>
							</label>
							<label className='flex flex-col gap-1 text-[13px] font-medium text-[#13335A]'>
								Início da vacância
								<input
									type='date'
									value={vacancy.dataInicio}
									onChange={(event) =>
										setVacancy((current) => ({
											...current,
											dataInicio: event.target.value,
										}))
									}
									className='h-[34px] rounded-md border border-[#E3E3E3] px-3 text-[13px] font-normal text-gray-600 outline-none'
								/>
							</label>
							<label className='flex flex-col gap-1 text-[13px] font-medium text-[#13335A]'>
								Área de atuação
								<select
									value={vacancy.categoria}
									onChange={(event) =>
										setVacancy((current) => ({
											...current,
											categoria: event.target.value,
										}))
									}
									className='h-[34px] rounded-md border border-[#E3E3E3] bg-white px-3 text-[13px] font-normal text-gray-600 outline-none'>
									<option value=''>Selecione</option>
									{categories.map((item) => (
										<option
											key={item.value}
											value={item.value}>
											{item.label}
										</option>
									))}
								</select>
							</label>
							<label className='flex flex-col gap-1 text-[13px] font-medium text-[#13335A]'>
								Vínculo
								<select
									value={vacancy.vinculoTipo}
									onChange={(event) =>
										setVacancy((current) => ({
											...current,
											vinculoTipo: event.target.value,
										}))
									}
									className='h-[34px] rounded-md border border-[#E3E3E3] bg-white px-3 text-[13px] font-normal text-gray-600 outline-none'>
									<option value=''>Selecione</option>
									{vinculoTypes.map((item) => (
										<option
											key={item.value}
											value={item.value}>
											{item.label}
										</option>
									))}
								</select>
							</label>
							<label className='flex flex-col gap-1 text-[13px] font-medium text-[#13335A]'>
								Regime
								<select
									value={vacancy.regime}
									onChange={(event) =>
										setVacancy((current) => ({
											...current,
											regime: event.target.value,
										}))
									}
									className='h-[34px] rounded-md border border-[#E3E3E3] bg-white px-3 text-[13px] font-normal text-gray-600 outline-none'>
									{availableRegimes.map((item) => (
										<option
											key={item.value}
											value={item.value}>
											{item.label}
										</option>
									))}
								</select>
							</label>
							<label className='flex flex-col gap-1 text-[13px] font-medium text-[#13335A]'>
								Recorrência
								<select
									value={vacancy.recorrencia}
									onChange={(event) =>
										setVacancy((current) => ({
											...current,
											recorrencia: event.target.value,
										}))
									}
									className='h-[34px] rounded-md border border-[#E3E3E3] bg-white px-3 text-[13px] font-normal text-gray-600 outline-none'>
									{recurrenceOptions.map((item) => (
										<option
											key={item.value}
											value={item.value}>
											{item.label}
										</option>
									))}
								</select>
							</label>
							<label className='sm:col-span-2 flex flex-col gap-1 text-[13px] font-medium text-[#13335A]'>
								Observação
								<textarea
									value={vacancy.observacao}
									onChange={(event) =>
										setVacancy((current) => ({
											...current,
											observacao: event.target.value,
										}))
									}
									rows='5'
									className='min-h-[105px] resize-none rounded-md border border-[#E3E3E3] px-3 py-2 text-[13px] font-normal text-gray-600 outline-none'
								/>
							</label>
							{error && (
								<p className='sm:col-span-2 text-center text-[12px] text-[#B42318]'>
									{error}
								</p>
							)}
							<div className='sm:col-span-2 flex justify-center pt-1'>
								<button
									type='submit'
									disabled={submitting}
									className='h-[30px] w-[170px] rounded-md border border-gray-200 text-[12px] text-[#7b7d80] shadow-sm hover:bg-gray-50 disabled:opacity-60'>
									{submitting ? 'Enviando...' : 'Enviar'}
								</button>
							</div>
						</div>
					</form>
				</div>
			)}
			{deleteTarget && (
				<div
					className='fixed inset-0 z-[90] font-inter flex items-center justify-center px-4 max-sm:items-center max-sm:overflow-hidden max-sm:px-3 max-sm:py-4'
					role='dialog'
					aria-modal='true'
					aria-labelledby='delete-multi-title'>
					<button
						type='button'
						className='absolute inset-0 bg-black/20'
						onClick={() => setDeleteTarget(null)}
						aria-label='Fechar modal de exclusão'
					/>
					<section className='relative max-h-[calc(100vh-2rem)] w-full max-w-[530px] overflow-y-auto overflow-x-hidden rounded-[14px] border border-[#E5E7EB] bg-white px-6 py-7 shadow-[0_10px_30px_rgba(15,23,42,0.14)] font-inter max-sm:max-h-[84vh] max-sm:max-w-[350px] max-sm:rounded-[10px] max-sm:px-4 max-sm:py-4'>
						<div className='flex items-center gap-4 max-sm:gap-3'>
							<div className='flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full bg-[#f0f0f0] max-sm:h-[40px] max-sm:w-[40px]'>
								<MdOutlineWarningAmber className='h-7 w-7 text-[#D9534F] max-sm:h-6 max-sm:w-6' />
							</div>
							<div className='min-w-0 pt-1'>
								<h2
									id='delete-multi-title'
									className='text-[17px] font-semibold text-[#4B5563] max-sm:text-[15px]'>
									Confirmar Exclusão
								</h2>
							</div>
						</div>
						<div className='mt-7 space-y-3 pl-[2px] max-sm:mt-4 max-sm:space-y-2'>
							<p className='text-[14px] leading-2 text-[#5F6368] max-sm:text-[13px]'>
								{isVacancyLine(deleteTarget)
									? 'Você confirma que deseja excluir esta vacância?'
									: deleteTarget?.forceSectorOnly
										? `Você tem certeza que deseja excluir ${getLineName(deleteTarget)} deste setor?`
										: `Você tem certeza que deseja excluir o profissional ${getLineName(deleteTarget)} da escala?`}
							</p>
							<p className='text-[14px] leading-2 text-[#5F6368] max-sm:text-[13px]'>
								Esta ação não pode ser desfeita
							</p>
						</div>
						<div className='mt-7 flex justify-end gap-3 max-sm:mt-5'>
							<button
								type='button'
								onClick={() => setDeleteTarget(null)}
								disabled={submitting}
								className='h-[34px] min-w-[96px] rounded-md border border-[#D8DEE8] bg-white px-4 text-[13px] font-medium text-[#5F6368] transition hover:bg-[#F8FAFC] disabled:opacity-60'>
								Cancelar
							</button>
							<button
								type='button'
								onClick={deleteProfessional}
								disabled={submitting}
								className='h-[34px] min-w-[82px] rounded-md bg-[#BD504E] px-4 text-[13px] font-medium text-white transition hover:bg-[#A94442] disabled:opacity-60'>
								{submitting ? 'Excluindo...' : 'Excluir'}
							</button>
						</div>
					</section>
				</div>
			)}
		</div>
	);
}

export default GerenciarProfissionaisMultiprofissional;
